import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, getDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../hooks/useAuth';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { PRODUCT_TYPES, PILE_MODELS, FABRIC_TYPES, MECHANISM_TYPES, SYSTEM_TYPES, MOTOR_TYPES, REMOTE_TYPES, ACCESSORIES, DIRECTION_TYPES, DIKEY_DIRECTION_TYPES, MOUNTING_TYPES, TUL_PILE_MODELS, KANAT_PILE_MODELS, KATLAMALI_PERDE_MEKANIZMA_TIPLERI, JALUZI_SISTEM_TIPLERI } from '../../constants';
import { formatPrice, calculatePileDensity } from '../../utils';
import { sendSingleOrderWhatsAppMessage } from '../../utils/whatsappUtils';
import './OrderForm.css';

const OrderForm = ({
  isAddingOrder,
  editingOrderId,
  selectedProductType,
  newOrder,
  isLoading,
  customerInfo,
  customerId,
  onProductTypeChange,
  onFieldChange,
  onSubmit,
  onCancel,
  pileModels = [] // Pile modelleri prop olarak gelebilir (OrderDetails'dan)
}) => {
  const { user } = useAuth();
  const [selectedFirma, setSelectedFirma] = useState('');
  const [selectedFirmaKumas, setSelectedFirmaKumas] = useState('');
  const [selectedFirmaMekanizma, setSelectedFirmaMekanizma] = useState('');
  const [selectedFirmaMotor, setSelectedFirmaMotor] = useState('');
  const [selectedFirmaAksesuar, setSelectedFirmaAksesuar] = useState('');
  const [selectedFirmaOzelTasarim, setSelectedFirmaOzelTasarim] = useState('');
  const [selectedFirmaAstar, setSelectedFirmaAstar] = useState('');

  const [lastSentFirma, setLastSentFirma] = useState(null);
  const [lastSentFirmaKumas, setLastSentFirmaKumas] = useState(null);
  const [lastSentFirmaMekanizma, setLastSentFirmaMekanizma] = useState(null);
  const [lastSentFirmaMotor, setLastSentFirmaMotor] = useState(null);
  const [lastSentFirmaAksesuar, setLastSentFirmaAksesuar] = useState(null);
  const [lastSentFirmaOzelTasarim, setLastSentFirmaOzelTasarim] = useState(null);
  const [lastSentFirmaAstar, setLastSentFirmaAstar] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [localPileModels, setLocalPileModels] = useState([]);
  const [productItems, setProductItems] = useState([]);

  const [showManualExtraCost, setShowManualExtraCost] = useState(false);
  const [showMountingCost, setShowMountingCost] = useState(false);
  const [showMotorCost, setShowMotorCost] = useState(false);
  const [showUnitCost, setShowUnitCost] = useState(false);
  const [showAccessoryCost, setShowAccessoryCost] = useState(false);
  const [showAyakCost, setShowAyakCost] = useState(false);
  const [showRustikRayCost, setShowRustikRayCost] = useState(false);
  const [showRustikAksesuarCost, setShowRustikAksesuarCost] = useState(false);
  const [showKanatAccessoryCost, setShowKanatAccessoryCost] = useState(false);
  const [showOzelTasarimUnitCost, setShowOzelTasarimUnitCost] = useState(false);
  const [showAstarUnitCost, setShowAstarUnitCost] = useState(false);

  // Özel Tasarım ve Astar Accordion açık/kapalı state'leri
  const [isOzelAccordionOpen, setIsOzelAccordionOpen] = useState(false);
  const [isAstarAccordionOpen, setIsAstarAccordionOpen] = useState(false);

  useEffect(() => {
    if (newOrder?.hasOzelTasarim || !!newOrder?.ozelTasarimProductName || (parseFloat(newOrder?.ozelTasarimTotalPrice) > 0)) {
      setIsOzelAccordionOpen(true);
    } else {
      setIsOzelAccordionOpen(false);
    }

    if (newOrder?.hasAstar || !!newOrder?.astarProductName || (parseFloat(newOrder?.astarPrice) > 0)) {
      setIsAstarAccordionOpen(true);
    } else {
      setIsAstarAccordionOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingOrderId, newOrder?.id]);

  const hasValidOzelTasarim = Boolean(
    newOrder?.hasOzelTasarim ||
    (parseFloat(newOrder?.ozelTasarimTotalPrice) > 0) ||
    newOrder?.ozelTasarimProductName ||
    (newOrder?.ozelTasarimUsedM2 && parseFloat(newOrder?.ozelTasarimUnitPrice) > 0)
  );

  const hasValidAstar = Boolean(
    newOrder?.hasAstar ||
    (parseFloat(newOrder?.astarPrice) > 0) ||
    newOrder?.astarProductName ||
    (newOrder?.usedM2 && parseFloat(newOrder?.astarUnitPrice) > 0)
  );

  const handleToggleOzelAccordion = () => {
    setIsOzelAccordionOpen(prev => {
      const nextVal = !prev;
      if (nextVal && !newOrder?.ozelTasarimQuantity) {
        onFieldChange('ozelTasarimQuantity', '1');
      }
      return nextVal;
    });
  };

  const handleToggleAstarAccordion = () => {
    setIsAstarAccordionOpen(prev => !prev);
  };

  // Sayısal alanlarda negatif işareti ve 'e' karakterini engelle
  const handleNumericKeyDown = (e) => {
    if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+') {
      e.preventDefault();
    }
  };

  const [adminSettings, setAdminSettings] = useState({
    sPilePrice: 0, sPileCost: 0, american2Price: 0, american2Cost: 0, american3Price: 0, american3Cost: 0,
    kanatSPilePrice: 0, kanatSPileCost: 0, kanatAmerican2Price: 0, kanatAmerican2Cost: 0, kanatAmerican3Price: 0, kanatAmerican3Cost: 0,
    kanatKapsulPrice: 0, kanatKapsulCost: 0,
    motorCurrency: 'USD', zigilPrice: 0, zigilCost: 0, reduktorPrice: 0, reduktorCost: 0, kurdelaPrice: 0, kurdelaCost: 0,
    metalZigilPrice: 0, metalZigilCost: 0, metalReduktorPrice: 0, metalReduktorCost: 0, ikiCamArasiPrice: 0, ikiCamArasiCost: 0,
    storJumboPrice: 0, storJumboCost: 0, storAltEtekDilimPrice: 0, storAltEtekDilimCost: 0, storKapaliKasaPrice: 0, storKapaliKasaCost: 0,
    storAltEtekLazerPrice: 0, storAltEtekLazerCost: 0, storDekoratifLamaPrice: 0, storDekoratifLamaCost: 0, storDekoratifKasaPrice: 0, storDekoratifKasaCost: 0,
    storAltEtekBeyazIsPrice: 0, storAltEtekBeyazIsCost: 0,
    storCiftliSistemPrice: 0, storCiftliSistemCost: 0,
    storLazerIslemeliPrice: 0, storLazerIslemeliCost: 0,
    storKulakliPrice: 0, storKulakliCost: 0,
    zebraJumboPrice: 0, zebraJumboCost: 0, zebraAltEtekDilimPrice: 0, zebraAltEtekDilimCost: 0, zebraKapaliKasaPrice: 0, zebraKapaliKasaCost: 0,
    dikeyFonPrice: 0, dikeyFonCost: 0, dikeyFonList: [],
    katlamaliMekanizmaList: [],
    kapsulPrice: 0,
    kapsulCost: 0,
    tulAltEtekKatlamaPrice: 0,
    tulAltEtekKatlamaCost: 0,
    rustikAyakModelList: [],
    rustikAyakCapList: [],
    rustikAyakUzunlukList: [],
    rustikAyakRenkKoduList: [],
    // Ürün bazlı motor ayarları
    storMotorPrice: 0, storMotorCost: 0, storMotorCurrency: 'USD', storMotorRate: 0,
    zebraMotorPrice: 0, zebraMotorCost: 0, zebraMotorCurrency: 'USD', zebraMotorRate: 0,
    ahsapJaluziMotorPrice: 0, ahsapJaluziMotorCost: 0, ahsapJaluziMotorCurrency: 'USD', ahsapJaluziMotorRate: 0,
    metalJaluziMotorPrice: 0, metalJaluziMotorCost: 0, metalJaluziMotorCurrency: 'USD', metalJaluziMotorRate: 0,
    dikeyPerdeMotorPrice: 0, dikeyPerdeMotorCost: 0, dikeyPerdeMotorCurrency: 'USD', dikeyPerdeMotorRate: 0,
    dikeyPvcMotorPrice: 0, dikeyPvcMotorCost: 0, dikeyPvcMotorCurrency: 'USD', dikeyPvcMotorRate: 0,
    dikeyStorMotorPrice: 0, dikeyStorMotorCost: 0, dikeyStorMotorCurrency: 'USD', dikeyStorMotorRate: 0,
    katlamaliPerdeMotorPrice: 0, katlamaliPerdeMotorCost: 0, katlamaliPerdeMotorCurrency: 'USD', katlamaliPerdeMotorRate: 0
  });
  const [exchangeRates, setExchangeRates] = useState({ USD: 1, EUR: 1 });

  // Ayarları ve Kurları Çek
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settingsRef = doc(db, 'settings', 'global');
        const docSnap = await getDoc(settingsRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setAdminSettings({
            sPilePrice: Number(data.tulSPilePrice ?? data.sPilePrice) || 0,
            sPileCost: Number(data.tulSPileCost ?? data.sPileCost) || 0,
            american2Price: Number(data.tulAmerican2Price ?? data.american2Price) || 0,
            american2Cost: Number(data.tulAmerican2Cost ?? data.american2Cost) || 0,
            american3Price: Number(data.tulAmerican3Price ?? data.american3Price) || 0,
            american3Cost: Number(data.tulAmerican3Cost ?? data.american3Cost) || 0,
            kanatSPilePrice: Number(data.kanatSPilePrice) || 0,
            kanatSPileCost: Number(data.kanatSPileCost) || 0,
            kanatAmerican2Price: Number(data.kanatAmerican2Price) || 0,
            kanatAmerican2Cost: Number(data.kanatAmerican2Cost) || 0,
            kanatAmerican3Price: Number(data.kanatAmerican3Price) || 0,
            kanatAmerican3Cost: Number(data.kanatAmerican3Cost) || 0,
            kanatKapsulPrice: Number(data.kanatKapsulPrice) || 0,
            kanatKapsulCost: Number(data.kanatKapsulCost) || 0,
            motorCurrency: data.motorCurrency || 'USD',
            zigilPrice: Number(data.zigilPrice) || 0,
            zigilCost: Number(data.zigilCost) || 0,
            reduktorPrice: Number(data.reduktorPrice) || 0,
            reduktorCost: Number(data.reduktorCost) || 0,
            kurdelaPrice: Number(data.kurdelaPrice) || 0,
            kurdelaCost: Number(data.kurdelaCost) || 0,
            metalZigilPrice: Number(data.metalZigilPrice) || 0,
            metalZigilCost: Number(data.metalZigilCost) || 0,
            metalReduktorPrice: Number(data.metalReduktorPrice) || 0,
            metalReduktorCost: Number(data.metalReduktorCost) || 0,
            ikiCamArasiPrice: Number(data.ikiCamArasiPrice) || 0,
            ikiCamArasiCost: Number(data.ikiCamArasiCost) || 0,
            storJumboPrice: Number(data.storJumboPrice) || 0,
            storJumboCost: Number(data.storJumboCost) || 0,
            storAltEtekDilimPrice: Number(data.storAltEtekDilimPrice) || 0,
            storAltEtekDilimCost: Number(data.storAltEtekDilimCost) || 0,
            storKapaliKasaPrice: Number(data.storKapaliKasaPrice) || 0,
            storKapaliKasaCost: Number(data.storKapaliKasaCost) || 0,
            storAltEtekLazerPrice: Number(data.storAltEtekLazerPrice) || 0,
            storAltEtekLazerCost: Number(data.storAltEtekLazerCost) || 0,
            storDekoratifLamaPrice: Number(data.storDekoratifLamaPrice) || 0,
            storDekoratifLamaCost: Number(data.storDekoratifLamaCost) || 0,
            storDekoratifKasaPrice: Number(data.storDekoratifKasaPrice) || 0,
            storDekoratifKasaCost: Number(data.storDekoratifKasaCost) || 0,
            storAltEtekBeyazIsPrice: Number(data.storAltEtekBeyazIsPrice) || 0,
            storAltEtekBeyazIsCost: Number(data.storAltEtekBeyazIsCost) || 0,
            storCiftliSistemPrice: Number(data.storCiftliSistemPrice) || 0,
            storCiftliSistemCost: Number(data.storCiftliSistemCost) || 0,
            storLazerIslemeliPrice: Number(data.storLazerIslemeliPrice) || 0,
            storLazerIslemeliCost: Number(data.storLazerIslemeliCost) || 0,
            storKulakliPrice: Number(data.storKulakliPrice) || 0,
            storKulakliCost: Number(data.storKulakliCost) || 0,
            zebraJumboPrice: Number(data.zebraJumboPrice) || 0,
            zebraJumboCost: Number(data.zebraJumboCost) || 0,
            zebraAltEtekDilimPrice: Number(data.zebraAltEtekDilimPrice) || 0,
            zebraAltEtekDilimCost: Number(data.zebraAltEtekDilimCost) || 0,
            zebraKapaliKasaPrice: Number(data.zebraKapaliKasaPrice) || 0,
            zebraKapaliKasaCost: Number(data.zebraKapaliKasaCost) || 0,
            dikeyFonPrice: Number(data.dikeyFonPrice) || 0,
            dikeyFonCost: Number(data.dikeyFonCost) || 0,
            dikeyFonList: data.dikeyFonList || [],
            katlamaliMekanizmaList: data.katlamaliMekanizmaList || [],
            kapsulPrice: Number(data.tulKapsulPrice ?? data.kapsulPrice) || 0,
            kapsulCost: Number(data.tulKapsulCost ?? data.kapsulCost) || 0,
            tulAltEtekKatlamaPrice: Number(data.tulAltEtekKatlamaPrice ?? data.altEtekKatlamaPrice) || 0,
            tulAltEtekKatlamaCost: Number(data.tulAltEtekKatlamaCost ?? data.altEtekKatlamaCost) || 0,
            rustikAyakModelList: data.rustikAyakModelList || [],
            rustikAyakCapList: data.rustikAyakCapList || [],
            rustikAyakUzunlukList: data.rustikAyakUzunlukList || [],
            rustikAyakRenkKoduList: data.rustikAyakRenkKoduList || [],
            // Ürün bazlı motor ayarları
            storMotorPrice: Number(data.storMotorPrice) || 0,
            storMotorCost: Number(data.storMotorCost) || 0,
            storMotorCurrency: data.storMotorCurrency || 'USD',
            storMotorRate: Number(data.storMotorRate) || 0,
            zebraMotorPrice: Number(data.zebraMotorPrice) || 0,
            zebraMotorCost: Number(data.zebraMotorCost) || 0,
            zebraMotorCurrency: data.zebraMotorCurrency || 'USD',
            zebraMotorRate: Number(data.zebraMotorRate) || 0,
            ahsapJaluziMotorPrice: Number(data.ahsapJaluziMotorPrice) || 0,
            ahsapJaluziMotorCost: Number(data.ahsapJaluziMotorCost) || 0,
            ahsapJaluziMotorCurrency: data.ahsapJaluziMotorCurrency || 'USD',
            ahsapJaluziMotorRate: Number(data.ahsapJaluziMotorRate) || 0,
            metalJaluziMotorPrice: Number(data.metalJaluziMotorPrice) || 0,
            metalJaluziMotorCost: Number(data.metalJaluziMotorCost) || 0,
            metalJaluziMotorCurrency: data.metalJaluziMotorCurrency || 'USD',
            metalJaluziMotorRate: Number(data.metalJaluziMotorRate) || 0,
            dikeyPerdeMotorPrice: Number(data.dikeyPerdeMotorPrice) || 0,
            dikeyPerdeMotorCost: Number(data.dikeyPerdeMotorCost) || 0,
            dikeyPerdeMotorCurrency: data.dikeyPerdeMotorCurrency || 'USD',
            dikeyPerdeMotorRate: Number(data.dikeyPerdeMotorRate) || 0,
            dikeyPvcMotorPrice: Number(data.dikeyPvcMotorPrice) || 0,
            dikeyPvcMotorCost: Number(data.dikeyPvcMotorCost) || 0,
            dikeyPvcMotorCurrency: data.dikeyPvcMotorCurrency || 'USD',
            dikeyPvcMotorRate: Number(data.dikeyPvcMotorRate) || 0,
            dikeyStorMotorPrice: Number(data.dikeyStorMotorPrice) || 0,
            dikeyStorMotorCost: Number(data.dikeyStorMotorCost) || 0,
            dikeyStorMotorCurrency: data.dikeyStorMotorCurrency || 'USD',
            dikeyStorMotorRate: Number(data.dikeyStorMotorRate) || 0,
            katlamaliPerdeMotorPrice: Number(data.katlamaliPerdeMotorPrice) || 0,
            katlamaliPerdeMotorCost: Number(data.katlamaliPerdeMotorCost) || 0,
            katlamaliPerdeMotorCurrency: data.katlamaliPerdeMotorCurrency || 'USD',
            katlamaliPerdeMotorRate: Number(data.katlamaliPerdeMotorRate) || 0
          });
        }
      } catch (err) { }
    };

    const fetchRates = async () => {
      try {
        const res = await fetch('https://open.er-api.com/v6/latest/USD');
        const data = await res.json();
        if (data && data.rates && data.rates.TRY && data.rates.EUR) {
          const usdToTry = data.rates.TRY;
          const eurToTry = data.rates.TRY / data.rates.EUR;
          setExchangeRates({ USD: usdToTry.toFixed(2), EUR: eurToTry.toFixed(2) });
        }
      } catch (err) { }
    };

    fetchSettings();
    fetchRates();
  }, []);

  // Plise seçilince otomatik bir boş satır ekle (eğer liste boşsa)
  useEffect(() => {
    if (selectedProductType === 'plise' && (!newOrder.pliseItems || newOrder.pliseItems.length === 0)) {
      onFieldChange('pliseItems', [{ id: Date.now(), width: '', height: '', usedM2: '1.00', quantity: '1', multisistem: false, rowTotal: '0.00' }]);
    }
  }, [selectedProductType, newOrder.pliseItems, onFieldChange]);



  // Firmaları Firestore'dan çek
  useEffect(() => {
    const fetchCompanies = async () => {
      if (!user?.uid) {
        setCompanies([]);
        return;
      }

      try {
        // Artık userId bazlı filtreleme yok, tüm firmaları çek
        const companiesSnapshot = await getDocs(collection(db, 'companies'));
        const companiesList = companiesSnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name,
          phone: doc.data().phone,
          defaultMessage: doc.data().defaultMessage || "Yeni sipariş bilgileri:"
        }));
        // İsme göre sırala
        companiesList.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setCompanies(companiesList);
      } catch (error) {
        console.error('Firmalar yüklenirken hata:', error);
        setCompanies([]);
      }
    };

    fetchCompanies();
  }, [user]);

  // Pile modellerini sadece constants'tan al (Firestore'dan çekme)
  useEffect(() => {
    // Sadece constants'taki modelleri kullan
    const constantsModels = Object.entries(
      selectedProductType === 'tul' ? TUL_PILE_MODELS : KANAT_PILE_MODELS
    ).map(([key, modelName]) => ({
      id: `const_${key} `,
      modelName: modelName,
      density: calculatePileDensity(modelName)
    }));
    setLocalPileModels(constantsModels);
  }, [selectedProductType]);

  // Ürün Listesi'nden ürünleri çek (ürün tipine göre filtrele)
  const fetchProductItems = useCallback(async (productType) => {
    if (!productType) {
      setProductItems([]);
      return;
    }
    try {
      const snapshot = await getDocs(collection(db, 'productItems'));
      const all = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      const filtered = all
        .filter(p => {
          if (productType === 'katlamaliPerde' || productType === 'kanat') {
            return p.productType === 'tul' || p.productType === 'kanat';
          }
          return p.productType === productType;
        })
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr'));
      setProductItems(filtered);
    } catch (error) {
      console.error('Ürün listesi yüklenirken hata:', error);
      setProductItems([]);
    }
  }, []);

  useEffect(() => {
    fetchProductItems(selectedProductType);
  }, [selectedProductType, fetchProductItems]);

  // Ürün seçilince adı ve birim fiyatı otomatik doldur
  const handleProductSelect = (productId) => {
    const product = productItems.find(p => p.id === productId);
    if (product) {
      onFieldChange('productName', product.name);

      // Eğer bir Jaluzi seçiliyse, unitPrice sistem tipine veya varsa ilk geçerli fiyata göre seçilmeli.
      let priceToSet = product.unitPrice;
      if (selectedProductType === 'ahsapJaluzi') {
        const pType = newOrder.sistemTipi;
        if (pType === JALUZI_SISTEM_TIPLERI.merdivenIpli) priceToSet = product.merdivenIpliFiyat || '';
        else if (pType === JALUZI_SISTEM_TIPLERI.kurdeleli) priceToSet = product.kurdeleliFiyat || '';
        else if (pType === JALUZI_SISTEM_TIPLERI.merdivenIpliReduktorlu) priceToSet = product.merdivenIpliReduktorluFiyat || '';
        else if (pType === JALUZI_SISTEM_TIPLERI.kurdelaliReduktorlu) priceToSet = product.kurdelaliReduktorluFiyat || '';
        else {
          // Sistem tipi henüz seçilmediyse, en mantıklı varsayılan fiyatı tablodan çek (kullanıcı tek fiyat girdiyse onu kullanır)
          priceToSet = product.merdivenIpliFiyat || product.kurdeleliFiyat || product.merdivenIpliReduktorluFiyat || product.kurdelaliReduktorluFiyat || '';
        }
      }

      let extra = 0;
      let extraCost = 0;
      if (selectedProductType === 'tul' || selectedProductType === 'kanat') {
        const isKanat = selectedProductType === 'kanat';
        const sPrice = isKanat ? adminSettings.kanatSPilePrice : adminSettings.sPilePrice;
        const sCost = isKanat ? adminSettings.kanatSPileCost : adminSettings.sPileCost;
        const am2Price = isKanat ? adminSettings.kanatAmerican2Price : adminSettings.american2Price;
        const am2Cost = isKanat ? adminSettings.kanatAmerican2Cost : adminSettings.american2Cost;
        const am3Price = isKanat ? adminSettings.kanatAmerican3Price : adminSettings.american3Price;
        const am3Cost = isKanat ? adminSettings.kanatAmerican3Cost : adminSettings.american3Cost;
        const kapPrice = isKanat ? adminSettings.kanatKapsulPrice : adminSettings.kapsulPrice;
        const kapCost = isKanat ? adminSettings.kanatKapsulCost : adminSettings.kapsulCost;

        if (newOrder.pileModel === 'S Pile') { extra = sPrice; extraCost = sCost; }
        if (newOrder.pileModel === "Amerikan Pile 2'li") { extra = am2Price; extraCost = am2Cost; }
        if (newOrder.pileModel === "Amerikan Pile 3'lü") { extra = am3Price; extraCost = am3Cost; }
        if (newOrder.pileModel === 'Kapsül Pile') {
          onFieldChange('capsuleUnitPrice', kapPrice || '');
          onFieldChange('capsuleUnitCost', kapCost || '');
        }

        if (selectedProductType === 'tul' && newOrder.hasAltEtekKatlama) {
          const altPrice = Number(adminSettings.tulAltEtekKatlamaPrice ?? adminSettings.altEtekKatlamaPrice) || 0;
          const altCost = Number(adminSettings.tulAltEtekKatlamaCost ?? adminSettings.altEtekKatlamaCost) || 0;
          extra += altPrice;
          extraCost += altCost;
        }
      } else if (selectedProductType === 'ahsapJaluzi') {
        if (newOrder.hasReduktor) { extra += adminSettings.reduktorPrice; extraCost += adminSettings.reduktorCost; }
        if (newOrder.hasKurdela) { extra += adminSettings.kurdelaPrice; extraCost += adminSettings.kurdelaCost; }
      } else if (selectedProductType === 'metalJaluzi') {
        if (newOrder.hasMetalReduktor) { extra += adminSettings.metalReduktorPrice; extraCost += adminSettings.metalReduktorCost; }
      } else if (selectedProductType === 'dikeyPerde') {
        if (newOrder.hasDikeyFon) { extra += adminSettings.dikeyFonPrice; extraCost += adminSettings.dikeyFonCost; }
      } else if (selectedProductType === 'katlamaliPerde') {
        onFieldChange('fabricPrice', priceToSet);
        onFieldChange('unitCost', product.cost || 0); // Kumaş maliyetini birim maliyete atayalım
      }
      // Maliyet hesaplama
      let costToSet = product.cost || 0;
      if (selectedProductType === 'ahsapJaluzi') {
        const pType = newOrder.sistemTipi;
        if (pType === JALUZI_SISTEM_TIPLERI.merdivenIpli) costToSet = product.merdivenIpliMaliyet || 0;
        else if (pType === JALUZI_SISTEM_TIPLERI.kurdeleli) costToSet = product.kurdeleliMaliyet || 0;
        else if (pType === JALUZI_SISTEM_TIPLERI.merdivenIpliReduktorlu) costToSet = product.merdivenIpliReduktorluMaliyet || 0;
        else if (pType === JALUZI_SISTEM_TIPLERI.kurdelaliReduktorlu) costToSet = product.kurdelaliReduktorluMaliyet || 0;
        else {
          costToSet = product.merdivenIpliMaliyet || product.kurdeleliMaliyet || product.merdivenIpliReduktorluMaliyet || product.kurdelaliReduktorluMaliyet || 0;
        }
      }
      onFieldChange('unitCost', Number(costToSet) + extraCost);
      onFieldChange('baseUnitCost', costToSet);

      onFieldChange('unitPrice', Number(priceToSet) + extra);
      onFieldChange('baseUnitPrice', priceToSet);
    } else {
      onFieldChange('productName', '');
      onFieldChange('unitPrice', '');
    }
  };

  const handlePliseProduct1Select = (productId) => {
    const product = productItems.find(p => p.id === productId);
    if (product) {
      onFieldChange('pliseProduct1Name', product.name);
      onFieldChange('pliseProduct1Price', product.unitPrice || '');
      if (product.cost) {
        onFieldChange('pliseProduct1Cost', product.cost);
      }
    } else {
      onFieldChange('pliseProduct1Name', '');
      onFieldChange('pliseProduct1Price', '');
    }
  };

  const handlePliseProduct2Select = (productId) => {
    const product = productItems.find(p => p.id === productId);
    if (product) {
      onFieldChange('pliseProduct2Name', product.name);
      onFieldChange('pliseProduct2Price', product.unitPrice || '');
      // Eger urun 1'in maliyeti yoksa urun 2'nin maliyetini kullanalim
      if (product.cost) {
        onFieldChange('pliseProduct2Cost', product.cost);
      }
    } else {
      onFieldChange('pliseProduct2Name', '');
      onFieldChange('pliseProduct2Price', '');
    }
  };

  const handleKatlamaliMechanismSelect = (mechanismType) => {
    const mechanism = adminSettings.katlamaliMekanizmaList.find(m => m.type === mechanismType);
    if (mechanism) {
      onFieldChange('mechanismType', mechanism.type);
      onFieldChange('mechanismPrice', mechanism.price);
      onFieldChange('mechanismCost', mechanism.cost || 0);
    } else {
      onFieldChange('mechanismType', mechanismType);
      onFieldChange('mechanismPrice', '');
      onFieldChange('mechanismCost', 0);
    }
  };

  const handleAddAccessory = () => {
    const currentAccessories = Array.isArray(newOrder.accessories) ? newOrder.accessories : [];
    const newAccessories = [...currentAccessories, { id: Date.now().toString(), name: '', price: '', cost: '0', quantity: '1', total: '0' }];
    onFieldChange('accessories', newAccessories);
  };

  const handleRemoveAccessory = (id) => {
    const currentAccessories = Array.isArray(newOrder.accessories) ? newOrder.accessories : [];
    const newAccessories = currentAccessories.filter(acc => acc.id !== id);
    onFieldChange('accessories', newAccessories);
  };

  const handleAccessoryChange = (id, field, value) => {
    const currentAccessories = Array.isArray(newOrder.accessories) ? newOrder.accessories : [];
    const newAccessories = currentAccessories.map(acc => {
      if (acc.id === id) {
        const updatedAcc = { ...acc, [field]: value };

        if (field === 'price' || field === 'quantity' || field === 'cost') {
          const p = parseFloat(updatedAcc.price) || 0;
          const q = parseFloat(updatedAcc.quantity) || 0;
          const c = parseFloat(updatedAcc.cost) || 0;
          updatedAcc.total = (p * q).toFixed(2);
          updatedAcc.costTotal = (c * q).toFixed(2);
        }
        return updatedAcc;
      }
      return acc;
    });
    onFieldChange('accessories', newAccessories);
  };

  const handleAddKanatAccessory = () => {
    const currentList = Array.isArray(newOrder.accessories) && newOrder.accessories.length > 0
      ? newOrder.accessories
      : (Array.isArray(newOrder.kanatAccessories) ? newOrder.kanatAccessories : []);
    const newAccessories = [...currentList, {
      id: Date.now().toString(),
      name: '',
      unitType: 'metre',
      meter: '1',
      quantity: '1',
      price: '',
      cost: '',
      total: '0',
      costTotal: '0'
    }];
    onFieldChange('accessories', newAccessories);
  };

  const handleRemoveKanatAccessory = (id) => {
    const currentList = Array.isArray(newOrder.accessories) && newOrder.accessories.length > 0
      ? newOrder.accessories
      : (Array.isArray(newOrder.kanatAccessories) ? newOrder.kanatAccessories : []);
    const newAccessories = currentList.filter(acc => acc.id !== id);
    onFieldChange('accessories', newAccessories);
  };

  const handleKanatAccessoryChange = (id, field, value) => {
    const currentList = Array.isArray(newOrder.accessories) && newOrder.accessories.length > 0
      ? newOrder.accessories
      : (Array.isArray(newOrder.kanatAccessories) ? newOrder.kanatAccessories : []);
    const newAccessories = currentList.map(acc => {
      if (acc.id === id) {
        const updatedAcc = { ...acc, [field]: value };

        if (field === 'unitType') {
          if (value === 'adet') {
            updatedAcc.meter = '';
          } else {
            updatedAcc.meter = '1';
          }
        }

        const p = parseFloat(updatedAcc.price) || 0;
        const q = parseFloat(updatedAcc.quantity) || 0;
        const c = parseFloat(updatedAcc.cost) || 0;

        if (updatedAcc.unitType === 'metre') {
          const m = parseFloat(updatedAcc.meter) || 0;
          updatedAcc.total = (m * q * p).toFixed(2);
          updatedAcc.costTotal = (m * q * c).toFixed(2);
        } else {
          updatedAcc.total = (q * p).toFixed(2);
          updatedAcc.costTotal = (q * c).toFixed(2);
        }

        return updatedAcc;
      }
      return acc;
    });
    onFieldChange('accessories', newAccessories);
  };

  // Özel Tasarım Kanat ürün seçimi
  const handleOzelProductSelect = (productId) => {
    const product = productItems.find(p => p.id === productId);
    if (product) {
      onFieldChange('hasOzelTasarim', true);
      onFieldChange('ozelTasarimProductName', product.name);

      let priceToSet = product.unitPrice || 0;
      let costToSet = product.cost || 0;

      let extra = 0;
      let extraCost = 0;
      const isKanat = product.productType === 'kanat';
      const sPrice = isKanat ? adminSettings.kanatSPilePrice : adminSettings.sPilePrice;
      const sCost = isKanat ? adminSettings.kanatSPileCost : adminSettings.sPileCost;
      const am2Price = isKanat ? adminSettings.kanatAmerican2Price : adminSettings.american2Price;
      const am2Cost = isKanat ? adminSettings.kanatAmerican2Cost : adminSettings.american2Cost;
      const am3Price = isKanat ? adminSettings.kanatAmerican3Price : adminSettings.american3Price;
      const am3Cost = isKanat ? adminSettings.kanatAmerican3Cost : adminSettings.american3Cost;
      const kapPrice = isKanat ? adminSettings.kanatKapsulPrice : adminSettings.kapsulPrice;
      const kapCost = isKanat ? adminSettings.kanatKapsulCost : adminSettings.kapsulCost;

      if (newOrder.ozelTasarimPileModel === 'S Pile') { extra = sPrice || 0; extraCost = sCost || 0; }
      if (newOrder.ozelTasarimPileModel === "Amerikan Pile 2'li") { extra = am2Price || 0; extraCost = am2Cost || 0; }
      if (newOrder.ozelTasarimPileModel === "Amerikan Pile 3'lü") { extra = am3Price || 0; extraCost = am3Cost || 0; }
      if (newOrder.ozelTasarimPileModel === 'Kapsül Pile') {
        onFieldChange('ozelTasarimCapsuleUnitPrice', kapPrice || '');
        onFieldChange('ozelTasarimCapsuleUnitCost', kapCost || '');
      }

      onFieldChange('ozelTasarimBaseUnitPrice', priceToSet);
      onFieldChange('ozelTasarimBaseUnitCost', costToSet);
      onFieldChange('ozelTasarimUnitPrice', Number(priceToSet) + extra);
      onFieldChange('ozelTasarimUnitCost', Number(costToSet) + extraCost);
    } else {
      onFieldChange('hasOzelTasarim', false);
      onFieldChange('ozelTasarimProductName', '');
      onFieldChange('ozelTasarimUnitPrice', '');
      onFieldChange('ozelTasarimUnitCost', '');
      onFieldChange('ozelTasarimBaseUnitPrice', '');
      onFieldChange('ozelTasarimBaseUnitCost', '');
      onFieldChange('ozelTasarimTotalPrice', 0);
    }
  };

  // Astar ürün seçimi
  const handleAstarProductSelect = (productId) => {
    const product = productItems.find(p => p.id === productId || p.name === productId);
    if (product) {
      onFieldChange('hasAstar', true);
      onFieldChange('astarProductName', product.name);
      onFieldChange('astarUnitPrice', product.unitPrice !== undefined ? product.unitPrice : (product.price || 0));
      onFieldChange('astarUnitCost', product.cost !== undefined ? product.cost : (product.unitCost || 0));
    } else {
      onFieldChange('hasAstar', false);
      onFieldChange('astarProductName', '');
      onFieldChange('astarUnitPrice', '');
      onFieldChange('astarUnitCost', '');
      onFieldChange('astarPrice', 0);
    }
  };

  // Ürün için unique key oluştur
  const createProductKey = useCallback(() => {
    const { roomName, productName, productType } = newOrder;
    if (!roomName || !productName || !productType) return 'temp';

    // Oda + Ürün Adı + Ürün Tipi kombinasyonu ile unique key
    return `${productType}-${roomName}-${productName}`.replace(/[^a-zA-Z0-9-]/g, '').toLowerCase();
  }, [newOrder]);

  // Component mount olduğunda ve ürün bilgileri değiştiğinde localStorage'dan verileri yükle
  useEffect(() => {
    if (customerId && !customerId.startsWith('temp_')) {
      // Ürün bazında unique key oluştur
      const productKey = createProductKey();
      if (productKey !== 'temp') {
        const savedData = localStorage.getItem(`whatsapp-${customerId}-${productKey}`);
        if (savedData) {
          try {
            const parsed = JSON.parse(savedData);
            setSelectedFirma(parsed.selectedFirmaId || '');
            setLastSentFirma(parsed.lastSentFirmaInfo || null);
            setSelectedFirmaKumas(parsed.selectedFirmaKumasId || parsed.selectedFirmaId || '');
            setLastSentFirmaKumas(parsed.lastSentFirmaKumasInfo || parsed.lastSentFirmaInfo || null);
            setSelectedFirmaMekanizma(parsed.selectedFirmaMekanizmaId || '');
            setLastSentFirmaMekanizma(parsed.lastSentFirmaMekanizmaInfo || null);
            setSelectedFirmaMotor(parsed.selectedFirmaMotorId || '');
            setLastSentFirmaMotor(parsed.lastSentFirmaMotorInfo || null);
            setSelectedFirmaAksesuar(parsed.selectedFirmaAksesuarId || '');
            setLastSentFirmaAksesuar(parsed.lastSentFirmaAksesuarInfo || null);
            setSelectedFirmaOzelTasarim(parsed.selectedFirmaOzelTasarimId || '');
            setLastSentFirmaOzelTasarim(parsed.lastSentFirmaOzelTasarimInfo || null);
            setSelectedFirmaAstar(parsed.selectedFirmaAstarId || '');
            setLastSentFirmaAstar(parsed.lastSentFirmaAstarInfo || null);
          } catch (error) {
            console.error('WhatsApp verileri yüklenirken hata:', error);
          }
        } else {
          // Bu ürün için kayıtlı veri yoksa sıfırla
          setSelectedFirma('');
          setLastSentFirma(null);
          setSelectedFirmaKumas('');
          setLastSentFirmaKumas(null);
          setSelectedFirmaMekanizma('');
          setLastSentFirmaMekanizma(null);
          setSelectedFirmaMotor('');
          setLastSentFirmaMotor(null);
          setSelectedFirmaAksesuar('');
          setLastSentFirmaAksesuar(null);
          setSelectedFirmaOzelTasarim('');
          setLastSentFirmaOzelTasarim(null);
          setSelectedFirmaAstar('');
          setLastSentFirmaAstar(null);
        }
      }
    }
  }, [customerId, createProductKey]);

  // WhatsApp gönderme fonksiyonu
  const handleWhatsAppSend = () => {
    if (!selectedFirma) {
      alert('Lütfen firma seçin!');
      return;
    }

    if (!customerInfo || !newOrder || !selectedProductType) {
      alert('Müşteri bilgileri veya sipariş bulunamadı!');
      return;
    }

    // Firestore'dan gelen firmaları kullan, yoksa constants'taki listeyi kullan
    const selectedFirmaInfo = companies.find(firma => firma.id === selectedFirma);
    if (selectedFirmaInfo) {
      // newOrder'a productType ekleyerek gönder
      const orderWithType = { ...newOrder, productType: selectedProductType };
      sendSingleOrderWhatsAppMessage(customerInfo, orderWithType, selectedFirmaInfo);

      // Son gönderilen firmayı kaydet
      setLastSentFirma(selectedFirmaInfo);

      // localStorage'a ürün bazında kaydet
      if (customerId && !customerId.startsWith('temp_')) {
        const productKey = createProductKey();
        const dataToSave = {
          selectedFirmaId: selectedFirma,
          lastSentFirmaInfo: selectedFirmaInfo
        };
        localStorage.setItem(`whatsapp-${customerId}-${productKey}`, JSON.stringify(dataToSave));
      }
    }
  };

  // Çoklu firma WhatsApp gönderme fonksiyonu (Katlamalı, Tül/Kanat aksesuar, özel tasarım, astar)
  const handleMultiWhatsAppSend = (subType) => {
    let firmaId = '';
    if (subType === 'kumas') firmaId = selectedFirmaKumas || selectedFirma;
    else if (subType === 'mekanizma') firmaId = selectedFirmaMekanizma;
    else if (subType === 'motor') firmaId = selectedFirmaMotor;
    else if (subType === 'aksesuar') firmaId = selectedFirmaAksesuar;
    else if (subType === 'ozelTasarim') firmaId = selectedFirmaOzelTasarim;
    else if (subType === 'astar') firmaId = selectedFirmaAstar;

    if (!firmaId) {
      alert('Lütfen firma seçin!');
      return;
    }

    if (!customerInfo || !newOrder || !selectedProductType) {
      alert('Müşteri bilgileri veya sipariş bulunamadı!');
      return;
    }

    const selectedFirmaInfo = companies.find(firma => firma.id === firmaId);
    if (selectedFirmaInfo) {
      const orderWithType = { ...newOrder, productType: selectedProductType };
      sendSingleOrderWhatsAppMessage(customerInfo, orderWithType, selectedFirmaInfo, subType);

      // Son gönderilene kaydet
      if (subType === 'kumas') {
        setLastSentFirmaKumas(selectedFirmaInfo);
        setLastSentFirma(selectedFirmaInfo);
      }
      else if (subType === 'mekanizma') setLastSentFirmaMekanizma(selectedFirmaInfo);
      else if (subType === 'motor') setLastSentFirmaMotor(selectedFirmaInfo);
      else if (subType === 'aksesuar') setLastSentFirmaAksesuar(selectedFirmaInfo);
      else if (subType === 'ozelTasarim') setLastSentFirmaOzelTasarim(selectedFirmaInfo);
      else if (subType === 'astar') setLastSentFirmaAstar(selectedFirmaInfo);

      // Tül/Kanat ve Katlamalı için localStorage güncelle
      if (customerId && !customerId.startsWith('temp_')) {
        const productKey = createProductKey();
        const savedRaw = localStorage.getItem(`whatsapp-${customerId}-${productKey}`);
        const existing = savedRaw ? JSON.parse(savedRaw) : {};
        if (subType === 'kumas') {
          existing.selectedFirmaId = firmaId;
          existing.lastSentFirmaInfo = selectedFirmaInfo;
        } else if (subType === 'mekanizma') {
          existing.selectedFirmaMekanizmaId = firmaId;
          existing.lastSentFirmaMekanizmaInfo = selectedFirmaInfo;
        } else if (subType === 'motor') {
          existing.selectedFirmaMotorId = firmaId;
          existing.lastSentFirmaMotorInfo = selectedFirmaInfo;
        } else if (subType === 'aksesuar') {
          existing.selectedFirmaAksesuarId = firmaId;
          existing.lastSentFirmaAksesuarInfo = selectedFirmaInfo;
        } else if (subType === 'ozelTasarim') {
          existing.selectedFirmaOzelTasarimId = firmaId;
          existing.lastSentFirmaOzelTasarimInfo = selectedFirmaInfo;
        } else if (subType === 'astar') {
          existing.selectedFirmaAstarId = firmaId;
          existing.lastSentFirmaAstarInfo = selectedFirmaInfo;
        }
        localStorage.setItem(`whatsapp-${customerId}-${productKey}`, JSON.stringify(existing));
      }
    }
  };

  // Katlamalı için geriye dönük uyumluluk takma adı
  const handleKatlamaliWhatsAppSend = handleMultiWhatsAppSend;

  const handleMotorTypeChange = (selectedMotor) => {
    onFieldChange('motorType', selectedMotor);
  };

  // react-select için stil özelleştirmesi
  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      padding: '0px 4px',
      minHeight: '42px',
      border: state.isFocused ? '1px solid #1a1a1a' : '1px solid #ddd',
      boxShadow: 'none',
      borderRadius: '6px',
      '&:hover': {
        border: state.isFocused ? '1px solid #1a1a1a' : '1px solid #cbd5e1'
      }
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0 8px',
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: '42px',
    }),
    input: (provided) => ({
      ...provided,
      margin: '0px',
      padding: '0px',
      'input:focus': {
        boxShadow: 'none !important'
      }
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#1a1a1a' : state.isFocused ? '#f0f0f0' : 'white',
      color: state.isSelected ? 'white' : '#333',
      cursor: 'pointer',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#333',
    })
  };

  // Ürün dropdown için ortak options objesi oluşturma
  const productOptions = selectedProductType === 'kanat'
    ? productItems.filter(p => p.productType === 'kanat').map(p => ({ value: p.id, label: p.name }))
    : productItems.map(p => ({ value: p.id, label: p.name }));

  const ozelTasarimProductOptions = selectedProductType === 'kanat'
    ? productItems.map(p => ({ value: p.id, label: `${p.name} (${p.productType === 'tul' ? 'Tül' : 'Kanat'})` }))
    : [];

  const astarProductOptions = productItems.filter(p => p.productType === 'kanat').map(p => ({ value: p.id, label: p.name }));

  // Firma bildirim kartları konfigürasyonu (Katlamalı, Tül/Kanat çoklu veya Standart)
  const isKatlamali = selectedProductType === 'katlamaliPerde';
  const isTulOrKanat = selectedProductType === 'tul' || selectedProductType === 'kanat';

  let currentFirmaCards = [];
  if (isKatlamali) {
    currentFirmaCards = [
      { id: 'kumas', label: 'Kumaş Firması', icon: 'fas fa-tshirt', color: '#6366f1', value: selectedFirmaKumas, setter: setSelectedFirmaKumas, lastSent: lastSentFirmaKumas, onSend: () => handleKatlamaliWhatsAppSend('kumas') },
      { id: 'mekanizma', label: 'Mekanizma Firması', icon: 'fas fa-cogs', color: '#0284c7', value: selectedFirmaMekanizma, setter: setSelectedFirmaMekanizma, lastSent: lastSentFirmaMekanizma, onSend: () => handleKatlamaliWhatsAppSend('mekanizma') },
      { id: 'motor', label: 'Motor Firması', icon: 'fas fa-bolt', color: '#ec4899', value: selectedFirmaMotor, setter: setSelectedFirmaMotor, lastSent: lastSentFirmaMotor, onSend: () => handleKatlamaliWhatsAppSend('motor') },
      { id: 'aksesuar', label: 'Aksesuar Firması', icon: 'fas fa-tags', color: '#10b981', value: selectedFirmaAksesuar, setter: setSelectedFirmaAksesuar, lastSent: lastSentFirmaAksesuar, onSend: () => handleKatlamaliWhatsAppSend('aksesuar') }
    ];
  } else if (isTulOrKanat) {
    const hasOzel = selectedProductType === 'kanat' && (newOrder.hasOzelTasarim || !!newOrder.ozelTasarimProductName);
    const hasAstar = selectedProductType === 'kanat' && (newOrder.hasAstar || !!newOrder.astarProductName);
    const hasAcc = (Array.isArray(newOrder.kanatAccessories) && newOrder.kanatAccessories.length > 0) || (Array.isArray(newOrder.accessories) && newOrder.accessories.length > 0);
    const isMulti = hasOzel || hasAstar || hasAcc;

    currentFirmaCards = [
      {
        id: 'kumas',
        label: isMulti ? 'Kumaş Firması' : (selectedProductType === 'tul' ? 'Tül Firması' : 'Kanat Kumaş Firması'),
        icon: 'fas fa-tshirt',
        color: '#6366f1',
        value: selectedFirma,
        setter: setSelectedFirma,
        lastSent: lastSentFirma,
        onSend: () => (isMulti ? handleMultiWhatsAppSend('kumas') : handleWhatsAppSend())
      },
      ...(hasOzel ? [
        { id: 'ozelTasarim', label: 'Özel Tasarım Firması', icon: 'fas fa-magic', color: '#f59e0b', value: selectedFirmaOzelTasarim, setter: setSelectedFirmaOzelTasarim, lastSent: lastSentFirmaOzelTasarim, onSend: () => handleMultiWhatsAppSend('ozelTasarim') }
      ] : []),
      ...(hasAstar ? [
        { id: 'astar', label: 'Astar Firması', icon: 'fas fa-layer-group', color: '#7c3aed', value: selectedFirmaAstar, setter: setSelectedFirmaAstar, lastSent: lastSentFirmaAstar, onSend: () => handleMultiWhatsAppSend('astar') }
      ] : []),
      ...(hasAcc ? [
        { id: 'aksesuar', label: 'Aksesuar Firması', icon: 'fas fa-tags', color: '#10b981', value: selectedFirmaAksesuar, setter: setSelectedFirmaAksesuar, lastSent: lastSentFirmaAksesuar, onSend: () => handleMultiWhatsAppSend('aksesuar') }
      ] : [])
    ];
  } else {
    currentFirmaCards = [
      { id: 'standart', label: 'Tedarikçi / İmalat Firması', icon: 'fas fa-building', color: '#2563eb', value: selectedFirma, setter: setSelectedFirma, lastSent: lastSentFirma, onSend: handleWhatsAppSend }
    ];
  }

  return (
    <div className="order-form">
      <h5>{editingOrderId ? 'Sipariş Düzenle' : 'Yeni Sipariş Ekle'}</h5>
      <form onSubmit={onSubmit}>
        {/* Ürün Tipi Seçimi */}
        <div className="form-group">
          <label>Ürün Tipi *</label>
          <select
            value={selectedProductType}
            onChange={(e) => onProductTypeChange(e.target.value)}
            required
            disabled={isLoading}
          >
            <option value="">Ürün tipi seçin</option>
            {Object.entries(PRODUCT_TYPES).map(([key, product]) => (
              <option key={key} value={key}>{product.label}</option>
            ))}
          </select>
        </div>

        {selectedProductType && (
          <div className="dynamic-fields">
            {/* Tül ve Kanat için özel alanlar */}
            {(selectedProductType === 'tul' || selectedProductType === 'kanat') && (
              <>
                <div className="form-group">
                  <label>Oda Adı *</label>
                  <input
                    type="text"
                    value={newOrder.roomName}
                    onChange={(e) => onFieldChange('roomName', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>Ürün Adı *</label>
                  <CreatableSelect
                    options={productOptions}
                    value={
                      productOptions.find(o => o.value === (productItems.find(p => p.name === newOrder.productName)?.id)) ||
                      (newOrder.productName ? { value: newOrder.productName, label: newOrder.productName } : null)
                    }
                    onChange={(option) => {
                      if (!option) {
                        onFieldChange('productName', '');
                        onFieldChange('unitPrice', '');
                        onFieldChange('unitCost', '');
                        onFieldChange('baseUnitPrice', '');
                        onFieldChange('baseUnitCost', '');
                      } else if (option.__isNew__) {
                        onFieldChange('productName', option.value);
                        onFieldChange('unitPrice', '');
                        onFieldChange('unitCost', '');
                        onFieldChange('baseUnitPrice', '');
                        onFieldChange('baseUnitCost', '');
                      } else {
                        handleProductSelect(option.value);
                      }
                    }}
                    isDisabled={isLoading}
                    isClearable
                    isSearchable
                    placeholder="Ürün seçin veya yazın..."
                    noOptionsMessage={() => "Ürün bulunamadı"}
                    formatCreateLabel={(inputValue) => `"${inputValue}" olarak ekle`}
                    styles={customSelectStyles}
                  />
                </div>

                <div className="form-group">
                  <label>Ürün Varyantı</label>
                  <input
                    type="text"
                    value={newOrder.productVariant || ''}
                    onChange={(e) => onFieldChange('productVariant', e.target.value)}
                    disabled={isLoading}
                    placeholder="Renk, desen vb. giriniz..."
                  />
                </div>

                <div className="form-group">
                  <label>En (cm) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newOrder.width}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('width', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>Boy (cm) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newOrder.height}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('height', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Birim Fiyat (₺) *
                    <i
                      className={`fas ${showUnitCost ? 'fa-unlock' : 'fa-lock'}`}
                      style={{
                        fontSize: '12px',
                        cursor: 'pointer',
                        color: showUnitCost ? '#1a1a1a' : '#ccc',
                        padding: '4px'
                      }}
                      onClick={() => setShowUnitCost(!showUnitCost)}
                      title="Maliyet girişi için tıkla"
                    ></i>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={newOrder.unitPrice}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => {
                      onFieldChange('unitPrice', e.target.value);
                      onFieldChange('baseUnitPrice', e.target.value); // Custom override resets baseline
                    }}
                    required
                    disabled={isLoading}
                  />
                </div>

                {showUnitCost && (
                  <div className="form-group animate-fadeIn">
                    <label style={{ color: '#1a1a1a' }}>Birim Maliyet (₺)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newOrder.unitCost}
                      onKeyDown={handleNumericKeyDown}
                      onChange={(e) => {
                        onFieldChange('unitCost', e.target.value);
                        onFieldChange('baseUnitCost', e.target.value);
                      }}
                      disabled={isLoading}
                      placeholder="Gizli maliyet"
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Pile Modeli *</label>
                  <select
                    value={newOrder.pileModel || ''}
                    onChange={(e) => {
                      const selectedModel = e.target.value;
                      onFieldChange('pileModel', selectedModel);

                      if ((selectedProductType === 'tul' || selectedProductType === 'kanat') && newOrder.baseUnitPrice !== undefined) {
                        let extra = 0;
                        let extraCost = 0;
                        const isKanat = selectedProductType === 'kanat';
                        const sPrice = isKanat ? adminSettings.kanatSPilePrice : adminSettings.sPilePrice;
                        const sCost = isKanat ? adminSettings.kanatSPileCost : adminSettings.sPileCost;
                        const am2Price = isKanat ? adminSettings.kanatAmerican2Price : adminSettings.american2Price;
                        const am2Cost = isKanat ? adminSettings.kanatAmerican2Cost : adminSettings.american2Cost;
                        const am3Price = isKanat ? adminSettings.kanatAmerican3Price : adminSettings.american3Price;
                        const am3Cost = isKanat ? adminSettings.kanatAmerican3Cost : adminSettings.american3Cost;
                        const kapPrice = isKanat ? adminSettings.kanatKapsulPrice : adminSettings.kapsulPrice;
                        const kapCost = isKanat ? adminSettings.kanatKapsulCost : adminSettings.kapsulCost;

                        if (selectedModel === 'S Pile') { extra = sPrice; extraCost = sCost; }
                        if (selectedModel === "Amerikan Pile 2'li") { extra = am2Price; extraCost = am2Cost; }
                        if (selectedModel === "Amerikan Pile 3'lü") { extra = am3Price; extraCost = am3Cost; }

                        // Kapsül Pile için özel birim fiyat ataması
                        if (selectedModel === 'Kapsül Pile') {
                          onFieldChange('capsuleUnitPrice', kapPrice || '');
                          onFieldChange('capsuleUnitCost', kapCost || '');
                        }

                        if (selectedProductType === 'tul' && newOrder.hasAltEtekKatlama) {
                          const altPrice = Number(adminSettings.tulAltEtekKatlamaPrice ?? adminSettings.altEtekKatlamaPrice) || 0;
                          const altCost = Number(adminSettings.tulAltEtekKatlamaCost ?? adminSettings.altEtekKatlamaCost) || 0;
                          extra += altPrice;
                          extraCost += altCost;
                        }

                        onFieldChange('unitPrice', Number(newOrder.baseUnitPrice || 0) + extra);
                        if (newOrder.baseUnitCost !== undefined && newOrder.baseUnitCost !== '') {
                          onFieldChange('unitCost', Number(newOrder.baseUnitCost) + extraCost);
                        } else {
                          // Eğer asıl maliyet girilmemişse, pile maliyetini tek başına yazıp kafa karıştırma
                          onFieldChange('unitCost', '');
                        }
                      }
                    }}
                    required
                    disabled={isLoading}
                  >
                    <option value="">Pile modeli seçin</option>
                    {localPileModels.map((model) => (
                      <option key={model.id} value={model.modelName}>
                        {model.modelName}
                      </option>
                    ))}
                  </select>
                </div>

                {newOrder.pileModel && !['Çima Dikiş', 'Düz Dikiş', 'Kumaş Güneşlik'].includes(newOrder.pileModel) && (
                  <div className="form-group">
                    <label>Pile Sıklığı</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0.1"
                      value={newOrder.pileDensity || ''}
                      onChange={(e) => onFieldChange('pileDensity', e.target.value)}
                      disabled={isLoading}
                      placeholder="Pile sıklığı"
                    />
                    <small>Pile sıklığını düzenleyebilirsiniz</small>
                  </div>
                )}

                {newOrder.usedM2 && (
                  <div className="form-group">
                    <label>Kullanılan Mt</label>
                    <input
                      type="text"
                      value={newOrder.usedM2}
                      readOnly
                      className="readonly-field"
                    />
                  </div>
                )}


                {newOrder.pileModel === 'Kapsül Pile' && (
                  <>
                    <div className="form-group">
                      <label>Kapsül Birim Fiyat (₺) *</label>
                      <input
                        type="number"
                        step="0.01"
                        value={newOrder.capsuleUnitPrice}
                        onChange={(e) => onFieldChange('capsuleUnitPrice', e.target.value)}
                        required
                        disabled={isLoading}
                      />
                    </div>

                    {newOrder.capsulePrice && (
                      <div className="form-group">
                        <label>Kapsül Ücreti (₺)</label>
                        <input
                          type="text"
                          value={formatPrice(newOrder.capsulePrice)}
                          readOnly
                          className="readonly-field"
                        />
                      </div>
                    )}


                  </>
                )}

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Ek Ücret (₺)
                    <i
                      className={`fas ${showManualExtraCost ? 'fa-unlock' : 'fa-lock'}`}
                      style={{
                        fontSize: '12px',
                        cursor: 'pointer',
                        color: showManualExtraCost ? '#1a1a1a' : '#ccc',
                        padding: '4px'
                      }}
                      onClick={() => setShowManualExtraCost(!showManualExtraCost)}
                      title="Maliyet girişi için tıkla"
                    ></i>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newOrder.extraPrice}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('extraPrice', e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                {showManualExtraCost && (
                  <div className="form-group animate-fadeIn">
                    <label style={{ color: '#1a1a1a' }}>Ek Ücret Maliyeti (₺)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newOrder.manualExtraCost}
                      onKeyDown={handleNumericKeyDown}
                      onChange={(e) => onFieldChange('manualExtraCost', e.target.value)}
                      disabled={isLoading}
                      placeholder="Gizli maliyet"
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Adet *</label>
                  <input
                    type="number"
                    step="1"
                    min="1"
                    value={newOrder.quantity || ''}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('quantity', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                {selectedProductType === 'tul' && (
                  <div className="form-group">
                    <label>&nbsp;</label>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        height: '42px',
                        padding: '0 12px',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        width: '100%',
                        backgroundColor: newOrder.hasAltEtekKatlama ? '#f1f5f9' : '#f8fafc',
                        borderColor: newOrder.hasAltEtekKatlama ? '#1a1a1a' : '#cbd5e1',
                        cursor: 'pointer',
                        margin: 0,
                        transition: 'all 0.2s ease'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.borderColor = '#1a1a1a';
                        e.currentTarget.style.backgroundColor = '#f1f5f9';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.borderColor = newOrder.hasAltEtekKatlama ? '#1a1a1a' : '#cbd5e1';
                        e.currentTarget.style.backgroundColor = newOrder.hasAltEtekKatlama ? '#f1f5f9' : '#f8fafc';
                      }}
                    >
                      <input
                        type="checkbox"
                        style={{
                          width: '16px',
                          height: '16px',
                          marginRight: '10px',
                          cursor: 'pointer',
                          accentColor: '#1a1a1a'
                        }}
                        checked={newOrder.hasAltEtekKatlama || false}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          onFieldChange('hasAltEtekKatlama', checked);
                          const altPrice = Number(adminSettings.tulAltEtekKatlamaPrice ?? adminSettings.altEtekKatlamaPrice) || 0;
                          const altCost = Number(adminSettings.tulAltEtekKatlamaCost ?? adminSettings.altEtekKatlamaCost) || 0;
                          onFieldChange('altEtekKatlamaAmount', checked ? altPrice : 0);
                          onFieldChange('altEtekKatlamaCostAmount', checked ? altCost : 0);

                          let pileExtra = 0;
                          let pileExtraCost = 0;
                          if (newOrder.pileModel === 'S Pile') {
                            pileExtra = Number(adminSettings.sPilePrice) || 0;
                            pileExtraCost = Number(adminSettings.sPileCost) || 0;
                          } else if (newOrder.pileModel === "Amerikan Pile 2'li") {
                            pileExtra = Number(adminSettings.american2Price) || 0;
                            pileExtraCost = Number(adminSettings.american2Cost) || 0;
                          } else if (newOrder.pileModel === "Amerikan Pile 3'lü") {
                            pileExtra = Number(adminSettings.american3Price) || 0;
                            pileExtraCost = Number(adminSettings.american3Cost) || 0;
                          }

                          const base = Number(newOrder.baseUnitPrice) || 0;
                          const extraAlt = checked ? altPrice : 0;
                          onFieldChange('unitPrice', base + pileExtra + extraAlt);

                          const baseCost = Number(newOrder.baseUnitCost) || 0;
                          const extraAltCost = checked ? altCost : 0;
                          if (newOrder.baseUnitCost !== undefined && newOrder.baseUnitCost !== '') {
                            onFieldChange('unitCost', baseCost + pileExtraCost + extraAltCost);
                          }
                        }}
                      />
                      <span style={{ fontWeight: '500', fontSize: '13px', color: '#1e293b' }}>
                        Alt Etek Katlama
                      </span>
                      {Number(adminSettings.tulAltEtekKatlamaPrice ?? adminSettings.altEtekKatlamaPrice) > 0 && (
                        <small style={{ color: '#0284c7', fontSize: '11px', marginLeft: '6px', fontWeight: 'bold' }}>
                          (+{adminSettings.tulAltEtekKatlamaPrice ?? adminSettings.altEtekKatlamaPrice} ₺)
                        </small>
                      )}
                    </label>
                  </div>
                )}

                {selectedProductType === 'kanat' && (
                  <>
                    {/* Özel Tasarım Accordion */}
                    <div className="ozel-tasarim-accordion" style={{ gridColumn: '1 / -1', marginTop: '10px', marginBottom: '10px' }}>
                      <div
                        onClick={handleToggleOzelAccordion}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '12px 15px',
                          backgroundColor: isOzelAccordionOpen ? '#1a1a1a' : (hasValidOzelTasarim ? '#1e293b' : '#f1f5f9'),
                          color: isOzelAccordionOpen || hasValidOzelTasarim ? 'white' : '#1e293b',
                          borderRadius: isOzelAccordionOpen ? '8px 8px 0 0' : '8px',
                          cursor: 'pointer',
                          fontWeight: '600',
                          fontSize: '14px',
                          transition: 'all 0.3s ease',
                          userSelect: 'none'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <i className="fas fa-magic" style={{ color: isOzelAccordionOpen || hasValidOzelTasarim ? '#f59e0b' : '#64748b' }}></i>
                          <span>Özel Tasarım Kanat</span>
                          {!isOzelAccordionOpen && hasValidOzelTasarim && (
                            <span style={{
                              fontSize: '11px',
                              padding: '2px 8px',
                              backgroundColor: '#f59e0b',
                              color: '#1a1a1a',
                              borderRadius: '10px',
                              fontWeight: '700'
                            }}>
                              {newOrder.ozelTasarimProductName ? `${newOrder.ozelTasarimProductName} • ` : ''}
                              {formatPrice(newOrder.ozelTasarimTotalPrice || 0)} ₺
                            </span>
                          )}
                        </div>
                        <i className={`fas fa-chevron-${isOzelAccordionOpen ? 'up' : 'down'}`}></i>
                      </div>

                      {isOzelAccordionOpen && (
                        <div
                          className="dynamic-fields"
                          style={{
                            padding: '20px 15px',
                            border: '1px solid #cbd5e1',
                            borderTop: 'none',
                            borderRadius: '0 0 8px 8px',
                            backgroundColor: '#fff',
                            marginTop: 0
                          }}
                        >
                          <div className="form-group">
                            <label>Ürün Seçimi *</label>
                            <CreatableSelect
                              options={ozelTasarimProductOptions}
                              value={
                                ozelTasarimProductOptions.find(o => o.value === (productItems.find(p => p.name === newOrder.ozelTasarimProductName)?.id)) ||
                                (newOrder.ozelTasarimProductName ? { value: newOrder.ozelTasarimProductName, label: newOrder.ozelTasarimProductName } : null)
                              }
                              onChange={(option) => {
                                if (!option) {
                                  onFieldChange('hasOzelTasarim', false);
                                  onFieldChange('ozelTasarimProductName', '');
                                  onFieldChange('ozelTasarimUnitPrice', '');
                                  onFieldChange('ozelTasarimUnitCost', '');
                                  onFieldChange('ozelTasarimBaseUnitPrice', '');
                                  onFieldChange('ozelTasarimBaseUnitCost', '');
                                  onFieldChange('ozelTasarimTotalPrice', 0);
                                } else if (option.__isNew__) {
                                  onFieldChange('hasOzelTasarim', true);
                                  onFieldChange('ozelTasarimProductName', option.value);
                                  onFieldChange('ozelTasarimUnitPrice', '');
                                  onFieldChange('ozelTasarimUnitCost', '');
                                  onFieldChange('ozelTasarimBaseUnitPrice', '');
                                  onFieldChange('ozelTasarimBaseUnitCost', '');
                                } else {
                                  handleOzelProductSelect(option.value);
                                }
                              }}
                              isDisabled={isLoading}
                              isClearable
                              isSearchable
                              placeholder="Seçin..."
                              noOptionsMessage={() => "Bulunamadı"}
                              formatCreateLabel={(inputValue) => `"${inputValue}" ekle`}
                              styles={customSelectStyles}
                            />
                          </div>

                          <div className="form-group">
                            <label>Ürün Varyantı</label>
                            <input
                              type="text"
                              value={newOrder.ozelTasarimProductVariant || ''}
                              onChange={(e) => onFieldChange('ozelTasarimProductVariant', e.target.value)}
                              placeholder="Renk, desen..."
                              disabled={isLoading}
                            />
                          </div>

                          <div className="form-group">
                            <label>En (cm) *</label>
                            <input
                              type="number"
                              step="0.01"
                              value={newOrder.ozelTasarimWidth || ''}
                              onKeyDown={handleNumericKeyDown}
                              onChange={(e) => onFieldChange('ozelTasarimWidth', e.target.value)}
                              disabled={isLoading}
                              required
                            />
                          </div>

                          <div className="form-group">
                            <label>Boy (cm) *</label>
                            <input
                              type="number"
                              step="0.01"
                              value={newOrder.ozelTasarimHeight || ''}
                              onKeyDown={handleNumericKeyDown}
                              onChange={(e) => onFieldChange('ozelTasarimHeight', e.target.value)}
                              disabled={isLoading}
                              required
                            />
                          </div>

                          <div className="form-group">
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              Birim Fiyat (₺) *
                              <i
                                className={`fas ${showOzelTasarimUnitCost ? 'fa-unlock' : 'fa-lock'}`}
                                style={{
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                  color: showOzelTasarimUnitCost ? '#1a1a1a' : '#ccc',
                                  padding: '4px'
                                }}
                                onClick={() => setShowOzelTasarimUnitCost(!showOzelTasarimUnitCost)}
                                title="Maliyet girişi için tıkla"
                              ></i>
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0.01"
                              value={newOrder.ozelTasarimUnitPrice || ''}
                              onKeyDown={handleNumericKeyDown}
                              onChange={(e) => {
                                onFieldChange('ozelTasarimUnitPrice', e.target.value);
                                onFieldChange('ozelTasarimBaseUnitPrice', e.target.value);
                              }}
                              disabled={isLoading}
                              required
                            />
                          </div>

                          {showOzelTasarimUnitCost && (
                            <div className="form-group animate-fadeIn">
                              <label style={{ color: '#1a1a1a' }}>Birim Maliyet (₺)</label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={newOrder.ozelTasarimUnitCost || ''}
                                onKeyDown={handleNumericKeyDown}
                                onChange={(e) => {
                                  onFieldChange('ozelTasarimUnitCost', e.target.value);
                                  onFieldChange('ozelTasarimBaseUnitCost', e.target.value);
                                }}
                                disabled={isLoading}
                                placeholder="Gizli maliyet"
                              />
                            </div>
                          )}

                          <div className="form-group">
                            <label>Pile Modeli *</label>
                            <select
                              value={newOrder.ozelTasarimPileModel || ''}
                              onChange={(e) => {
                                const selectedModel = e.target.value;
                                onFieldChange('ozelTasarimPileModel', selectedModel);

                                const ozelProduct = productItems.find(p => p.name === newOrder.ozelTasarimProductName);
                                const isKanat = ozelProduct ? ozelProduct.productType === 'kanat' : true;
                                const sPrice = isKanat ? adminSettings.kanatSPilePrice : adminSettings.sPilePrice;
                                const sCost = isKanat ? adminSettings.kanatSPileCost : adminSettings.sPileCost;
                                const am2Price = isKanat ? adminSettings.kanatAmerican2Price : adminSettings.american2Price;
                                const am2Cost = isKanat ? adminSettings.kanatAmerican2Cost : adminSettings.american2Cost;
                                const am3Price = isKanat ? adminSettings.kanatAmerican3Price : adminSettings.american3Price;
                                const am3Cost = isKanat ? adminSettings.kanatAmerican3Cost : adminSettings.american3Cost;
                                const kapPrice = isKanat ? adminSettings.kanatKapsulPrice : adminSettings.kapsulPrice;
                                const kapCost = isKanat ? adminSettings.kanatKapsulCost : adminSettings.kapsulCost;

                                if (newOrder.ozelTasarimBaseUnitPrice !== undefined) {
                                  let extra = 0;
                                  let extraCost = 0;
                                  if (selectedModel === 'S Pile') { extra = sPrice || 0; extraCost = sCost || 0; }
                                  if (selectedModel === "Amerikan Pile 2'li") { extra = am2Price || 0; extraCost = am2Cost || 0; }
                                  if (selectedModel === "Amerikan Pile 3'lü") { extra = am3Price || 0; extraCost = am3Cost || 0; }

                                  if (selectedModel === 'Kapsül Pile') {
                                    onFieldChange('ozelTasarimCapsuleUnitPrice', kapPrice || '');
                                    onFieldChange('ozelTasarimCapsuleUnitCost', kapCost || '');
                                  }

                                  onFieldChange('ozelTasarimUnitPrice', Number(newOrder.ozelTasarimBaseUnitPrice || 0) + extra);
                                  if (newOrder.ozelTasarimBaseUnitCost !== undefined && newOrder.ozelTasarimBaseUnitCost !== '') {
                                    onFieldChange('ozelTasarimUnitCost', Number(newOrder.ozelTasarimBaseUnitCost) + extraCost);
                                  } else {
                                    onFieldChange('ozelTasarimUnitCost', '');
                                  }
                                }
                              }}
                              disabled={isLoading}
                              required
                            >
                              <option value="">Seçin...</option>
                              {localPileModels.map((model) => (
                                <option key={model.id} value={model.modelName}>
                                  {model.modelName}
                                </option>
                              ))}
                            </select>
                          </div>

                          {newOrder.ozelTasarimPileModel && !['Çima Dikiş', 'Düz Dikiş', 'Kumaş Güneşlik'].includes(newOrder.ozelTasarimPileModel) && (
                            <div className="form-group">
                              <label>Pile Sıklığı</label>
                              <input
                                type="number"
                                step="0.1"
                                min="0.1"
                                value={newOrder.ozelTasarimPileDensity || ''}
                                onChange={(e) => onFieldChange('ozelTasarimPileDensity', e.target.value)}
                                disabled={isLoading}
                                placeholder="Sıklık"
                              />
                            </div>
                          )}

                          {newOrder.ozelTasarimUsedM2 && (
                            <div className="form-group">
                              <label>Kullanılan Mt</label>
                              <input
                                type="text"
                                value={newOrder.ozelTasarimUsedM2}
                                readOnly
                                className="readonly-field"
                              />
                            </div>
                          )}

                          <div className="form-group">
                            <label>Ek Ücret (₺)</label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={newOrder.ozelTasarimExtraPrice || ''}
                              onKeyDown={handleNumericKeyDown}
                              onChange={(e) => onFieldChange('ozelTasarimExtraPrice', e.target.value)}
                              disabled={isLoading}
                            />
                          </div>

                          <div className="form-group">
                            <label>Adet *</label>
                            <input
                              type="number"
                              step="1"
                              min="1"
                              value={newOrder.ozelTasarimQuantity || ''}
                              onKeyDown={handleNumericKeyDown}
                              onChange={(e) => onFieldChange('ozelTasarimQuantity', e.target.value)}
                              disabled={isLoading}
                              required
                            />
                          </div>

                          {newOrder.ozelTasarimUsedM2 && newOrder.ozelTasarimUnitPrice && (
                            <div className="form-group">
                              <label>Özel Tasarım Tutarı (₺)</label>
                              <input
                                type="text"
                                value={formatPrice(newOrder.ozelTasarimTotalPrice || 0)}
                                readOnly
                                className="readonly-field"
                              />
                              <small style={{ color: '#64748b', fontSize: '0.78rem' }}>
                                {newOrder.ozelTasarimUsedM2} mt × {formatPrice(newOrder.ozelTasarimUnitPrice)} ₺
                                {(parseFloat(newOrder.ozelTasarimQuantity) || 1) > 1 ? ` × ${newOrder.ozelTasarimQuantity} adet` : ''}
                              </small>
                            </div>
                          )}

                          <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', marginTop: '10px', paddingTop: '10px', borderTop: '1px dashed #e2e8f0' }}>
                            <button
                              type="button"
                              onClick={() => {
                                onFieldChange('hasOzelTasarim', false);
                                onFieldChange('ozelTasarimProductName', '');
                                onFieldChange('ozelTasarimProductVariant', '');
                                onFieldChange('ozelTasarimWidth', '');
                                onFieldChange('ozelTasarimHeight', '');
                                onFieldChange('ozelTasarimUnitPrice', '');
                                onFieldChange('ozelTasarimUnitCost', '');
                                onFieldChange('ozelTasarimBaseUnitPrice', '');
                                onFieldChange('ozelTasarimBaseUnitCost', '');
                                onFieldChange('ozelTasarimPileModel', '');
                                onFieldChange('ozelTasarimPileDensity', '');
                                onFieldChange('ozelTasarimUsedM2', '');
                                onFieldChange('ozelTasarimExtraPrice', '');
                                onFieldChange('ozelTasarimQuantity', '');
                                onFieldChange('ozelTasarimTotalPrice', 0);
                                setIsOzelAccordionOpen(false);
                              }}
                              style={{
                                background: '#fef2f2',
                                border: '1px solid #fee2e2',
                                color: '#ef4444',
                                fontSize: '12px',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontWeight: '500',
                                padding: '6px 12px',
                                borderRadius: '6px',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <i className="fas fa-trash-alt"></i> Özel Tasarımı Kaldır
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Astar Accordion */}
                    <div className="astar-accordion" style={{ gridColumn: '1 / -1', marginTop: '10px', marginBottom: '10px' }}>
                      <div
                        onClick={handleToggleAstarAccordion}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '12px 15px',
                          backgroundColor: isAstarAccordionOpen ? '#7c3aed' : (hasValidAstar ? '#6d28d9' : '#f1f5f9'),
                          color: isAstarAccordionOpen || hasValidAstar ? 'white' : '#1e293b',
                          borderRadius: isAstarAccordionOpen ? '8px 8px 0 0' : '8px',
                          cursor: 'pointer',
                          fontWeight: '600',
                          fontSize: '14px',
                          transition: 'all 0.3s ease',
                          userSelect: 'none'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <i className="fas fa-layer-group" style={{ color: isAstarAccordionOpen || hasValidAstar ? '#c4b5fd' : '#64748b' }}></i>
                          <span>Astar</span>
                          {!isAstarAccordionOpen && hasValidAstar && (
                            <span style={{
                              fontSize: '11px',
                              padding: '2px 8px',
                              backgroundColor: '#c4b5fd',
                              color: '#4c1d95',
                              borderRadius: '10px',
                              fontWeight: '700'
                            }}>
                              {newOrder.astarProductName ? `${newOrder.astarProductName} • ` : ''}
                              {formatPrice(newOrder.astarPrice || 0)} ₺
                            </span>
                          )}
                        </div>
                        <i className={`fas fa-chevron-${isAstarAccordionOpen ? 'up' : 'down'}`}></i>
                      </div>

                      {isAstarAccordionOpen && (
                        <div
                          className="dynamic-fields"
                          style={{
                            padding: '20px 15px',
                            border: '1px solid #cbd5e1',
                            borderTop: 'none',
                            borderRadius: '0 0 8px 8px',
                            backgroundColor: '#fff',
                            marginTop: 0
                          }}
                        >
                          <div className="form-group">
                            <label>Ürün Adı *</label>
                            <CreatableSelect
                              options={astarProductOptions}
                              value={
                                astarProductOptions.find(o => o.value === (productItems.find(p => p.name === newOrder.astarProductName)?.id)) ||
                                (newOrder.astarProductName ? { value: newOrder.astarProductName, label: newOrder.astarProductName } : null)
                              }
                              onChange={(option) => {
                                if (!option) {
                                  onFieldChange('hasAstar', false);
                                  onFieldChange('astarProductName', '');
                                  onFieldChange('astarUnitPrice', '');
                                  onFieldChange('astarUnitCost', '');
                                  onFieldChange('astarPrice', 0);
                                } else if (option.__isNew__) {
                                  onFieldChange('hasAstar', true);
                                  onFieldChange('astarProductName', option.value);
                                  onFieldChange('astarUnitPrice', '');
                                  onFieldChange('astarUnitCost', '');
                                } else {
                                  handleAstarProductSelect(option.value);
                                }
                              }}
                              isDisabled={isLoading}
                              isClearable
                              isSearchable
                              placeholder="Kanat ürünü seçin veya yazın..."
                              noOptionsMessage={() => "Kanat ürünü bulunamadı"}
                              formatCreateLabel={(inputValue) => `"${inputValue}" ekle`}
                              styles={customSelectStyles}
                            />
                          </div>

                          <div className="form-group">
                            <label>Ürün Varyantı</label>
                            <input
                              type="text"
                              value={newOrder.astarProductVariant || ''}
                              onChange={(e) => onFieldChange('astarProductVariant', e.target.value)}
                              placeholder="Renk, desen..."
                              disabled={isLoading}
                            />
                          </div>

                          <div className="form-group">
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              Birim Fiyat (₺) *
                              <i
                                className={`fas ${showAstarUnitCost ? 'fa-unlock' : 'fa-lock'}`}
                                style={{
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                  color: showAstarUnitCost ? '#1a1a1a' : '#ccc',
                                  padding: '4px'
                                }}
                                onClick={() => setShowAstarUnitCost(!showAstarUnitCost)}
                                title="Maliyet girişi için tıkla"
                              ></i>
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={newOrder.astarUnitPrice || ''}
                              onKeyDown={handleNumericKeyDown}
                              onChange={(e) => onFieldChange('astarUnitPrice', e.target.value)}
                              disabled={isLoading}
                              required
                            />
                          </div>

                          {showAstarUnitCost && (
                            <div className="form-group animate-fadeIn">
                              <label style={{ color: '#1a1a1a' }}>Birim Maliyet (₺)</label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={newOrder.astarUnitCost || ''}
                                onKeyDown={handleNumericKeyDown}
                                onChange={(e) => onFieldChange('astarUnitCost', e.target.value)}
                                disabled={isLoading}
                                placeholder="Gizli maliyet"
                              />
                            </div>
                          )}

                          {newOrder.usedM2 && newOrder.astarUnitPrice && (
                            <div className="form-group">
                              <label>Astar Ücreti (₺)</label>
                              <input
                                type="text"
                                value={formatPrice(newOrder.astarPrice || 0)}
                                readOnly
                                className="readonly-field"
                              />
                              <small style={{ color: '#64748b', fontSize: '0.78rem' }}>
                                {newOrder.usedM2} mt × {formatPrice(newOrder.astarUnitPrice)} ₺
                                {(parseFloat(newOrder.quantity) || 1) > 1 ? ` × ${newOrder.quantity} adet` : ''}
                              </small>
                            </div>
                          )}

                          <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', marginTop: '10px', paddingTop: '10px', borderTop: '1px dashed #e2e8f0' }}>
                            <button
                              type="button"
                              onClick={() => {
                                onFieldChange('hasAstar', false);
                                onFieldChange('astarProductName', '');
                                onFieldChange('astarProductVariant', '');
                                onFieldChange('astarUnitPrice', '');
                                onFieldChange('astarUnitCost', '');
                                onFieldChange('astarPrice', 0);
                                onFieldChange('astarCost', 0);
                                setIsAstarAccordionOpen(false);
                              }}
                              style={{
                                background: '#fef2f2',
                                border: '1px solid #fee2e2',
                                color: '#ef4444',
                                fontSize: '12px',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontWeight: '500',
                                padding: '6px 12px',
                                borderRadius: '6px',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <i className="fas fa-trash-alt"></i> Astarı Kaldır
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Aksesuarlar Bölümü */}
                {(selectedProductType === 'kanat' || selectedProductType === 'tul') && (
                  <div style={{ gridColumn: '1 / -1', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '16px', marginBottom: '15px', backgroundColor: '#fff' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <h6 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#0f172a' }}>🎗️ Aksesuarlar</h6>
                      <button
                        type="button"
                        onClick={handleAddKanatAccessory}
                        style={{ padding: '6px 12px', backgroundColor: '#0f172a', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center' }}
                      >
                        <i className="fas fa-plus" style={{ marginRight: '5px' }}></i>
                        Aksesuar Ekle
                      </button>
                    </div>

                    {(Array.isArray(newOrder.accessories) && newOrder.accessories.length > 0) || (Array.isArray(newOrder.kanatAccessories) && newOrder.kanatAccessories.length > 0) ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {(Array.isArray(newOrder.accessories) && newOrder.accessories.length > 0 ? newOrder.accessories : newOrder.kanatAccessories).map((acc) => {
                          const isMetre = acc.unitType === 'metre';
                          const gridCols = isMetre
                            ? '1.5fr 0.8fr 0.5fr 0.5fr 1.6fr 1fr 40px'
                            : '1.5fr 0.8fr 0.5fr 1.6fr 1fr 40px';
                          return (
                            <div
                              key={acc.id}
                              className="aksesuar-item-row"
                              style={{
                                display: 'grid',
                                gridTemplateColumns: gridCols,
                                gap: '10px',
                                alignItems: 'end',
                                paddingBottom: '10px',
                                borderBottom: '1px solid #f1f5f9'
                              }}
                            >
                              <div className="form-group" style={{ marginBottom: 0 }}>
                                <label style={{ fontSize: '11px' }}>Aksesuar Adı</label>
                                <input
                                  type="text"
                                  value={acc.name}
                                  onChange={(e) => handleKanatAccessoryChange(acc.id, 'name', e.target.value)}
                                  placeholder="Bordür, Saçak, Kolbağı, Renso vb."
                                  style={{ padding: '6px' }}
                                />
                              </div>
                              <div className="form-group" style={{ marginBottom: 0 }}>
                                <label style={{ fontSize: '11px' }}>Birim Tipi</label>
                                <select
                                  value={acc.unitType || 'metre'}
                                  onChange={(e) => handleKanatAccessoryChange(acc.id, 'unitType', e.target.value)}
                                  style={{ padding: '6px' }}
                                >
                                  <option value="metre">Metre</option>
                                  <option value="adet">Adet</option>
                                </select>
                              </div>
                              {isMetre && (
                                <div className="form-group" style={{ marginBottom: 0 }}>
                                  <label style={{ fontSize: '11px' }}>Metre</label>
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={acc.meter}
                                    onKeyDown={handleNumericKeyDown}
                                    onChange={(e) => handleKanatAccessoryChange(acc.id, 'meter', e.target.value)}
                                    style={{ padding: '6px' }}
                                  />
                                </div>
                              )}
                              <div className="form-group" style={{ marginBottom: 0 }}>
                                <label style={{ fontSize: '11px' }}>Adet</label>
                                <input
                                  type="number"
                                  step="1"
                                  min="1"
                                  value={acc.quantity}
                                  onKeyDown={handleNumericKeyDown}
                                  onChange={(e) => handleKanatAccessoryChange(acc.id, 'quantity', e.target.value)}
                                  style={{ padding: '6px' }}
                                />
                              </div>
                              <div className="form-group" style={{ marginBottom: 0 }}>
                                <label style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  {showKanatAccessoryCost ? 'Fiyat / Maliyet' : 'Fiyat (₺)'}
                                  <i
                                    className={`fas ${showKanatAccessoryCost ? 'fa-unlock' : 'fa-lock'}`}
                                    style={{ fontSize: '10px', cursor: 'pointer', color: showKanatAccessoryCost ? '#1a1a1a' : '#ccc' }}
                                    onClick={() => setShowKanatAccessoryCost(!showKanatAccessoryCost)}
                                    title="Maliyet girişi için tıkla"
                                  ></i>
                                </label>
                                <div style={{ display: 'flex', gap: '5px' }}>
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={acc.price}
                                    onKeyDown={handleNumericKeyDown}
                                    onChange={(e) => handleKanatAccessoryChange(acc.id, 'price', e.target.value)}
                                    style={{ padding: '6px', width: '100%' }}
                                    placeholder="Fiyat"
                                  />
                                  {showKanatAccessoryCost && (
                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      placeholder="Maliyet"
                                      value={acc.cost || ''}
                                      onKeyDown={handleNumericKeyDown}
                                      onChange={(e) => handleKanatAccessoryChange(acc.id, 'cost', e.target.value)}
                                      style={{ padding: '6px', fontSize: '13px', border: '1px solid #1a1a1a', width: '100%', borderRadius: '4px' }}
                                    />
                                  )}
                                </div>
                              </div>
                              <div className="form-group" style={{ marginBottom: 0 }}>
                                <label style={{ fontSize: '11px' }}>Tutar (₺)</label>
                                <input
                                  type="text"
                                  value={acc.total}
                                  readOnly
                                  style={{ padding: '6px', backgroundColor: '#f1f5f9' }}
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => handleRemoveKanatAccessory(acc.id)}
                                style={{ padding: '8px', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', marginBottom: '4px' }}
                                title="Aksesuarı Sil"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p style={{ fontSize: '13px', color: '#64748b', textAlign: 'center', margin: '10px 0' }}>Henüz aksesuar eklenmemiş.</p>
                    )}
                  </div>
                )}

                <div className="form-group">
                  <label>İskonto (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={newOrder.discount}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('discount', e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                {newOrder.totalPrice && (
                  <div className="form-group">
                    <label>Toplam Tutar (₺)</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.totalPrice)}
                      readOnly
                      className="total-price"
                    />
                  </div>
                )}

                {newOrder.discountedPrice && parseFloat(newOrder.discount) > 0 && (
                  <div className="form-group">
                    <label>İndirimli Tutar (₺)</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.discountedPrice)}
                      readOnly
                      className="discounted-price"
                    />
                  </div>
                )}
              </>
            )}

            {/* Stor ve Zebra için özel alanlar */}
            {(selectedProductType === 'stor' || selectedProductType === 'zebra') && (
              <>
                <div className="form-group">
                  <label>Oda Adı *</label>
                  <input
                    type="text"
                    value={newOrder.roomName}
                    onChange={(e) => onFieldChange('roomName', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>Ürün Adı *</label>
                  <CreatableSelect
                    options={productOptions}
                    value={
                      productOptions.find(o => o.value === (productItems.find(p => p.name === newOrder.productName)?.id)) ||
                      (newOrder.productName ? { value: newOrder.productName, label: newOrder.productName } : null)
                    }
                    onChange={(option) => {
                      if (!option) {
                        onFieldChange('productName', '');
                        onFieldChange('unitPrice', '');
                      } else if (option.__isNew__) {
                        onFieldChange('productName', option.value);
                        onFieldChange('unitPrice', '');
                      } else {
                        handleProductSelect(option.value);
                      }
                    }}
                    isDisabled={isLoading}
                    isClearable
                    isSearchable
                    placeholder="Ürün seçin veya yazın..."
                    noOptionsMessage={() => "Ürün bulunamadı"}
                    formatCreateLabel={(inputValue) => `"${inputValue}" olarak ekle`}
                    styles={customSelectStyles}
                  />
                </div>

                <div className="form-group">
                  <label>Ürün Varyantı</label>
                  <input
                    type="text"
                    value={newOrder.productVariant || ''}
                    onChange={(e) => onFieldChange('productVariant', e.target.value)}
                    disabled={isLoading}
                    placeholder="Renk, desen vb. giriniz..."
                  />
                </div>

                <div className="form-group">
                  <label>En (cm) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newOrder.width}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('width', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>Boy (cm) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newOrder.height}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('height', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>Yön *</label>
                  <select
                    value={newOrder.direction || ''}
                    onChange={(e) => onFieldChange('direction', e.target.value)}
                    required
                    disabled={isLoading}
                  >
                    <option value="">Seçiniz</option>
                    {Object.entries(DIRECTION_TYPES).map(([key, value]) => (
                      <option key={key} value={value}>{value}</option>
                    ))}
                  </select>
                </div>

                {newOrder.usedM2 && (
                  <div className="form-group">
                    <label>Kullanılan Mt²</label>
                    <input
                      type="text"
                      value={newOrder.usedM2}
                      readOnly
                      className="readonly-field"
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Birim Fiyat (₺) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={newOrder.unitPrice}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('unitPrice', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                {(selectedProductType === 'stor' || selectedProductType === 'zebra') && (
                  <>
                    <div className="form-group">
                      <label>Motor</label>
                      <select
                        value={newOrder.motorType || ''}
                        onChange={(e) => handleMotorTypeChange(e.target.value)}
                        disabled={isLoading}
                      >
                        <option value="">Seçiniz</option>
                        {Object.entries(MOTOR_TYPES).map(([key, value]) => (
                          <option key={key} value={value}>{value}</option>
                        ))}
                      </select>
                    </div>

                    {newOrder.motorType && (
                      <>
                        <div className="form-group">
                          <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            Motor Ücreti ({adminSettings[`${selectedProductType}MotorCurrency`] || 'USD'})
                            <i
                              className={`fas ${showMotorCost ? "fa-unlock" : "fa-lock"}`}
                              style={{ fontSize: "12px", cursor: "pointer", color: showMotorCost ? "#1a1a1a" : "#ccc", padding: "4px" }}
                              onClick={() => setShowMotorCost(!showMotorCost)}
                              title="Maliyet girişi için tıkla"
                            ></i>
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={newOrder.motorPriceCurrency || ''}
                            onKeyDown={handleNumericKeyDown}
                            onChange={(e) => onFieldChange('motorPriceCurrency', e.target.value)}
                            disabled={isLoading}
                          />
                          {newOrder.motorPriceCurrency && (
                            <small style={{ display: 'block', marginTop: '5px', color: '#1a1a1a' }}>
                              ≈ {newOrder.motorPrice ? Number(newOrder.motorPrice).toFixed(2) : 0} ₺
                            </small>
                          )}
                        </div>

                        {showMotorCost && (
                          <div className="form-group animate-fadeIn">
                            <label style={{ color: "#1a1a1a" }}>
                              Motor Maliyeti ({adminSettings[`${selectedProductType}MotorCurrency`] || 'USD'})
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={newOrder.motorCostCurrency !== undefined && newOrder.motorCostCurrency !== null ? newOrder.motorCostCurrency : ''}
                              onKeyDown={handleNumericKeyDown}
                              onChange={(e) => onFieldChange("motorCostCurrency", e.target.value)}
                              disabled={isLoading}
                              placeholder="Gizli maliyet"
                            />
                            {newOrder.motorCostCurrency && (
                              <small style={{ display: 'block', marginTop: '5px', color: '#1a1a1a' }}>
                                ≈ {newOrder.motorCost ? Number(newOrder.motorCost).toFixed(2) : 0} ₺
                              </small>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}

                <div className="form-group">
                  <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>Montaj (₺)<i className={`fas ${showMountingCost ? "fa-unlock" : "fa-lock"}`} style={{ fontSize: "12px", cursor: "pointer", color: showMountingCost ? "#1a1a1a" : "#ccc", padding: "4px" }} onClick={() => setShowMountingCost(!showMountingCost)} title="Maliyet girişi için tıkla"></i></label>
                  <input
                    type="number"
                    step="0.01"
                    value={newOrder.mounting || ''}
                    onChange={(e) => onFieldChange('mounting', e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                {showMountingCost && (
                  <div className="form-group animate-fadeIn">
                    <label style={{ color: "#1a1a1a" }}>Montaj Maliyeti (₺)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newOrder.mountingCost}
                      onKeyDown={handleNumericKeyDown}
                      onChange={(e) => onFieldChange("mountingCost", e.target.value)}
                      disabled={isLoading}
                      placeholder="Gizli maliyet"
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Adet *</label>
                  <input
                    type="number"
                    step="1"
                    min="1"
                    value={newOrder.quantity || ''}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('quantity', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>İskonto (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={newOrder.discount}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('discount', e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px', marginTop: '15px', marginBottom: '15px' }}>
                  {selectedProductType === 'stor' && (
                    <>
                      <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
                        <label style={{ display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', backgroundColor: '#f8fafc', cursor: 'pointer', margin: 0, transition: 'all 0.2s ease' }}>
                          <input type="checkbox" style={{ width: '16px', height: '16px', marginRight: '10px', cursor: 'pointer', accentColor: '#1a1a1a' }} checked={newOrder.hasStorJumbo || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasStorJumbo', checked); onFieldChange('storJumboAmount', checked ? adminSettings.storJumboPrice : 0); onFieldChange('storJumboCostAmount', checked ? adminSettings.storJumboCost : 0); }} />
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155' }}>Jumbo Sistem</span>
                            <small style={{ color: '#1a1a1a', fontSize: '11px' }}>{adminSettings.storJumboPrice} ₺</small>
                          </div>
                        </label>
                      </div>
                      <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
                        <label style={{ display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', backgroundColor: '#f8fafc', cursor: 'pointer', margin: 0, transition: 'all 0.2s ease' }}>
                          <input type="checkbox" style={{ width: '16px', height: '16px', marginRight: '10px', cursor: 'pointer', accentColor: '#1a1a1a' }} checked={newOrder.hasStorAltEtekDilim || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasStorAltEtekDilim', checked); onFieldChange('storAltEtekDilimAmount', checked ? adminSettings.storAltEtekDilimPrice : 0); onFieldChange('storAltEtekDilimCostAmount', checked ? adminSettings.storAltEtekDilimCost : 0); }} />
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155' }}>Alt Etek Dilim</span>
                            <small style={{ color: '#1a1a1a', fontSize: '11px' }}>{adminSettings.storAltEtekDilimPrice} ₺</small>
                          </div>
                        </label>
                      </div>
                      <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
                        <label style={{ display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', backgroundColor: '#f8fafc', cursor: 'pointer', margin: 0, transition: 'all 0.2s ease' }}>
                          <input type="checkbox" style={{ width: '16px', height: '16px', marginRight: '10px', cursor: 'pointer', accentColor: '#1a1a1a' }} checked={newOrder.hasStorKapaliKasa || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasStorKapaliKasa', checked); onFieldChange('storKapaliKasaAmount', checked ? adminSettings.storKapaliKasaPrice : 0); onFieldChange('storKapaliKasaCostAmount', checked ? adminSettings.storKapaliKasaCost : 0); }} />
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155' }}>Kapalı Kasa</span>
                            <small style={{ color: '#1a1a1a', fontSize: '11px' }}>{adminSettings.storKapaliKasaPrice} ₺</small>
                          </div>
                        </label>
                      </div>
                      <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
                        <label style={{ display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', backgroundColor: '#f8fafc', cursor: 'pointer', margin: 0, transition: 'all 0.2s ease' }}>
                          <input type="checkbox" style={{ width: '16px', height: '16px', marginRight: '10px', cursor: 'pointer', accentColor: '#1a1a1a' }} checked={newOrder.hasStorAltEtekLazer || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasStorAltEtekLazer', checked); onFieldChange('storAltEtekLazerAmount', checked ? adminSettings.storAltEtekLazerPrice : 0); onFieldChange('storAltEtekLazerCostAmount', checked ? adminSettings.storAltEtekLazerCost : 0); }} />
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155' }}>Alt Etek Lazer</span>
                            <small style={{ color: '#1a1a1a', fontSize: '11px' }}>{adminSettings.storAltEtekLazerPrice} ₺</small>
                          </div>
                        </label>
                      </div>
                      <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
                        <label style={{ display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', backgroundColor: '#f8fafc', cursor: 'pointer', margin: 0, transition: 'all 0.2s ease' }}>
                          <input type="checkbox" style={{ width: '16px', height: '16px', marginRight: '10px', cursor: 'pointer', accentColor: '#1a1a1a' }} checked={newOrder.hasStorDekoratifLama || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasStorDekoratifLama', checked); onFieldChange('storDekoratifLamaAmount', checked ? adminSettings.storDekoratifLamaPrice : 0); onFieldChange('storDekoratifLamaCostAmount', checked ? adminSettings.storDekoratifLamaCost : 0); }} />
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155' }}>Dekoratif Lama</span>
                            <small style={{ color: '#1a1a1a', fontSize: '11px' }}>{adminSettings.storDekoratifLamaPrice} ₺</small>
                          </div>
                        </label>
                      </div>
                      <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
                        <label style={{ display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', backgroundColor: '#f8fafc', cursor: 'pointer', margin: 0, transition: 'all 0.2s ease' }}>
                          <input type="checkbox" style={{ width: '16px', height: '16px', marginRight: '10px', cursor: 'pointer', accentColor: '#1a1a1a' }} checked={newOrder.hasStorDekoratifKasa || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasStorDekoratifKasa', checked); onFieldChange('storDekoratifKasaAmount', checked ? adminSettings.storDekoratifKasaPrice : 0); onFieldChange('storDekoratifKasaCostAmount', checked ? adminSettings.storDekoratifKasaCost : 0); }} />
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155' }}>Dekoratif Kasa</span>
                            <small style={{ color: '#1a1a1a', fontSize: '11px' }}>{adminSettings.storDekoratifKasaPrice} ₺</small>
                          </div>
                        </label>
                      </div>
                      <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
                        <label style={{ display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', backgroundColor: '#f8fafc', cursor: 'pointer', margin: 0, transition: 'all 0.2s ease' }}>
                          <input type="checkbox" style={{ width: '16px', height: '16px', marginRight: '10px', cursor: 'pointer', accentColor: '#1a1a1a' }} checked={newOrder.hasStorAltEtekBeyazIs || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasStorAltEtekBeyazIs', checked); onFieldChange('storAltEtekBeyazIsAmount', checked ? adminSettings.storAltEtekBeyazIsPrice : 0); onFieldChange('storAltEtekBeyazIsCostAmount', checked ? adminSettings.storAltEtekBeyazIsCost : 0); }} />
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155' }}>Alt Etek Beyaz İş</span>
                            <small style={{ color: '#1a1a1a', fontSize: '11px' }}>{adminSettings.storAltEtekBeyazIsPrice} ₺</small>
                          </div>
                        </label>
                      </div>
                      <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
                        <label style={{ display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', backgroundColor: '#f8fafc', cursor: 'pointer', margin: 0, transition: 'all 0.2s ease' }}>
                          <input type="checkbox" style={{ width: '16px', height: '16px', marginRight: '10px', cursor: 'pointer', accentColor: '#1a1a1a' }} checked={newOrder.hasStorCiftliSistem || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasStorCiftliSistem', checked); onFieldChange('storCiftliSistemAmount', checked ? adminSettings.storCiftliSistemPrice : 0); onFieldChange('storCiftliSistemCostAmount', checked ? adminSettings.storCiftliSistemCost : 0); }} />
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155' }}>Çiftli Sistem</span>
                            <small style={{ color: '#1a1a1a', fontSize: '11px' }}>{adminSettings.storCiftliSistemPrice} ₺</small>
                          </div>
                        </label>
                      </div>
                      <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
                        <label style={{ display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', backgroundColor: '#f8fafc', cursor: 'pointer', margin: 0, transition: 'all 0.2s ease' }}>
                          <input type="checkbox" style={{ width: '16px', height: '16px', marginRight: '10px', cursor: 'pointer', accentColor: '#1a1a1a' }} checked={newOrder.hasStorLazerIslemeli || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasStorLazerIslemeli', checked); onFieldChange('storLazerIslemeliAmount', checked ? adminSettings.storLazerIslemeliPrice : 0); onFieldChange('storLazerIslemeliCostAmount', checked ? adminSettings.storLazerIslemeliCost : 0); }} />
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155' }}>Lazer İşlemeli</span>
                            <small style={{ color: '#1a1a1a', fontSize: '11px' }}>{adminSettings.storLazerIslemeliPrice} ₺</small>
                          </div>
                        </label>
                      </div>
                      <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
                        <label style={{ display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', backgroundColor: '#f8fafc', cursor: 'pointer', margin: 0, transition: 'all 0.2s ease' }}>
                          <input type="checkbox" style={{ width: '16px', height: '16px', marginRight: '10px', cursor: 'pointer', accentColor: '#1a1a1a' }} checked={newOrder.hasStorKulakli || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasStorKulakli', checked); onFieldChange('storKulakliAmount', checked ? adminSettings.storKulakliPrice : 0); onFieldChange('storKulakliCostAmount', checked ? adminSettings.storKulakliCost : 0); }} />
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155' }}>Kulaklı</span>
                            <small style={{ color: '#1a1a1a', fontSize: '11px' }}>{adminSettings.storKulakliPrice} ₺</small>
                          </div>
                        </label>
                      </div>
                    </>
                  )}

                  {selectedProductType === 'zebra' && (
                    <>
                      <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
                        <label style={{ display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', backgroundColor: '#f8fafc', cursor: 'pointer', margin: 0, transition: 'all 0.2s ease' }}>
                          <input type="checkbox" style={{ width: '16px', height: '16px', marginRight: '10px', cursor: 'pointer', accentColor: '#1a1a1a' }} checked={newOrder.hasZebraJumbo || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasZebraJumbo', checked); onFieldChange('zebraJumboAmount', checked ? adminSettings.zebraJumboPrice : 0); onFieldChange('zebraJumboCostAmount', checked ? adminSettings.zebraJumboCost : 0); }} />
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155' }}>Jumbo Sistem (Zebra)</span>
                            <small style={{ color: '#1a1a1a', fontSize: '11px' }}>{adminSettings.zebraJumboPrice} ₺</small>
                          </div>
                        </label>
                      </div>
                      <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
                        <label style={{ display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', backgroundColor: '#f8fafc', cursor: 'pointer', margin: 0, transition: 'all 0.2s ease' }}>
                          <input type="checkbox" style={{ width: '16px', height: '16px', marginRight: '10px', cursor: 'pointer', accentColor: '#1a1a1a' }} checked={newOrder.hasZebraAltEtekDilim || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasZebraAltEtekDilim', checked); onFieldChange('zebraAltEtekDilimAmount', checked ? adminSettings.zebraAltEtekDilimPrice : 0); onFieldChange('zebraAltEtekDilimCostAmount', checked ? adminSettings.zebraAltEtekDilimCost : 0); }} />
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155' }}>Alt Etek Dilim (Zebra)</span>
                            <small style={{ color: '#1a1a1a', fontSize: '11px' }}>{adminSettings.zebraAltEtekDilimPrice} ₺</small>
                          </div>
                        </label>
                      </div>
                      <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
                        <label style={{ display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', backgroundColor: '#f8fafc', cursor: 'pointer', margin: 0, transition: 'all 0.2s ease' }}>
                          <input type="checkbox" style={{ width: '16px', height: '16px', marginRight: '10px', cursor: 'pointer', accentColor: '#1a1a1a' }} checked={newOrder.hasZebraKapaliKasa || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasZebraKapaliKasa', checked); onFieldChange('zebraKapaliKasaAmount', checked ? adminSettings.zebraKapaliKasaPrice : 0); onFieldChange('zebraKapaliKasaCostAmount', checked ? adminSettings.zebraKapaliKasaCost : 0); }} />
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155' }}>Kapalı Kasa (Zebra)</span>
                            <small style={{ color: '#1a1a1a', fontSize: '11px' }}>{adminSettings.zebraKapaliKasaPrice} ₺</small>
                          </div>
                        </label>
                      </div>
                    </>
                  )}
                </div>


                {newOrder.totalPrice && (
                  <div className="form-group">
                    <label>Toplam Tutar (₺)</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.totalPrice)}
                      readOnly
                      className="total-price"
                    />
                  </div>
                )}

                {newOrder.discountedPrice && parseFloat(newOrder.discount) > 0 && (
                  <div className="form-group">
                    <label>İndirimli Tutar (₺)</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.discountedPrice)}
                      readOnly
                      className="discounted-price"
                    />
                  </div>
                )}
              </>
            )}

            {/* Ahşap Jaluzi için özel alanlar */}
            {selectedProductType === 'ahsapJaluzi' && (
              <>
                <div className="form-group">
                  <label>Oda Adı *</label>
                  <input type="text" value={newOrder.roomName} onChange={(e) => onFieldChange('roomName', e.target.value)} required disabled={isLoading} />
                </div>
                <div className="form-group">
                  <label>Ürün Adı *</label>
                  <CreatableSelect
                    options={productOptions}
                    value={
                      productOptions.find(o => o.value === (productItems.find(p => p.name === newOrder.productName)?.id)) ||
                      (newOrder.productName ? { value: newOrder.productName, label: newOrder.productName } : null)
                    }
                    onChange={(option) => {
                      if (!option) {
                        onFieldChange('productName', '');
                        onFieldChange('unitPrice', '');
                      } else if (option.__isNew__) {
                        onFieldChange('productName', option.value);
                        onFieldChange('unitPrice', '');
                      } else {
                        handleProductSelect(option.value);
                      }
                    }}
                    isDisabled={isLoading}
                    isClearable
                    isSearchable
                    placeholder="Ürün seçin veya yazın..."
                    noOptionsMessage={() => "Ürün bulunamadı"}
                    formatCreateLabel={(inputValue) => `"${inputValue}" olarak ekle`}
                    styles={customSelectStyles}
                  />
                </div>
                <div className="form-group">
                  <label>Ürün Varyantı</label>
                  <input
                    type="text"
                    value={newOrder.productVariant || ''}
                    onChange={(e) => onFieldChange('productVariant', e.target.value)}
                    disabled={isLoading}
                    placeholder="Renk, desen vb. giriniz..."
                  />
                </div>
                <div className="form-group">
                  <label>Sistem Tipi *</label>
                  <Select
                    options={Object.entries(JALUZI_SISTEM_TIPLERI).map(([key, value]) => ({ value: value, label: value }))}
                    value={newOrder.sistemTipi ? { value: newOrder.sistemTipi, label: newOrder.sistemTipi } : null}
                    onChange={(option) => {
                      const selectedSys = option ? option.value : '';
                      onFieldChange('sistemTipi', selectedSys);
                      const currentProduct = productItems.find(p => p.name === newOrder.productName);

                      if (currentProduct) {
                        let newPrice = '';
                        let newCost = 0;

                        if (selectedSys === JALUZI_SISTEM_TIPLERI.merdivenIpli) {
                          newPrice = currentProduct.merdivenIpliFiyat || '';
                          newCost = currentProduct.merdivenIpliMaliyet || 0;
                        } else if (selectedSys === JALUZI_SISTEM_TIPLERI.kurdeleli) {
                          newPrice = currentProduct.kurdeleliFiyat || '';
                          newCost = currentProduct.kurdeleliMaliyet || 0;
                        } else if (selectedSys === JALUZI_SISTEM_TIPLERI.merdivenIpliReduktorlu) {
                          newPrice = currentProduct.merdivenIpliReduktorluFiyat || '';
                          newCost = currentProduct.merdivenIpliReduktorluMaliyet || 0;
                        } else if (selectedSys === JALUZI_SISTEM_TIPLERI.kurdelaliReduktorlu) {
                          newPrice = currentProduct.kurdelaliReduktorluFiyat || '';
                          newCost = currentProduct.kurdelaliReduktorluMaliyet || 0;
                        } else {
                          newPrice = currentProduct.merdivenIpliFiyat || currentProduct.kurdeleliFiyat || currentProduct.merdivenIpliReduktorluFiyat || currentProduct.kurdelaliReduktorluFiyat || '';
                          newCost = currentProduct.merdivenIpliMaliyet || currentProduct.kurdeleliMaliyet || currentProduct.merdivenIpliReduktorluMaliyet || currentProduct.kurdelaliReduktorluMaliyet || 0;
                        }

                        let extraVars = 0;
                        let extraCosts = 0;
                        if (newOrder.hasReduktor) {
                          extraVars += adminSettings.reduktorPrice;
                          extraCosts += adminSettings.reduktorCost;
                        }
                        if (newOrder.hasKurdela) {
                          extraVars += adminSettings.kurdelaPrice;
                          extraCosts += adminSettings.kurdelaCost;
                        }

                        onFieldChange('baseUnitPrice', newPrice);
                        onFieldChange('unitPrice', Number(newPrice) + extraVars);
                        onFieldChange('baseUnitCost', newCost);
                        onFieldChange('unitCost', Number(newCost) + extraCosts);
                      }
                    }}
                    isDisabled={isLoading} isClearable isSearchable placeholder="Sistem Tipi Seçin" noOptionsMessage={() => "Sistem Tipi bulunamadı"} filterOption={(option, inputValue) => option.label.toLowerCase().includes(inputValue.toLowerCase())} styles={customSelectStyles}
                  />
                </div>
                <div className="form-group">
                  <label>En (cm) *</label>
                  <input type="number" step="0.01" onKeyDown={handleNumericKeyDown} value={newOrder.width} onChange={(e) => onFieldChange('width', e.target.value)} required disabled={isLoading} />
                </div>
                <div className="form-group">
                  <label>Boy (cm) *</label>
                  <input type="number" step="0.01" onKeyDown={handleNumericKeyDown} value={newOrder.height} onChange={(e) => onFieldChange('height', e.target.value)} required disabled={isLoading} />
                </div>
                {newOrder.usedM2 && (
                  <div className="form-group">
                    <label>Kullanılan Mt²</label>
                    <input type="text" value={newOrder.usedM2} readOnly className="readonly-field" />
                  </div>
                )}
                <div className="form-group">
                  <label>Yön *</label>
                  <select value={newOrder.direction || ''} onChange={(e) => onFieldChange('direction', e.target.value)} required disabled={isLoading}>
                    <option value="">Seçiniz</option>
                    {Object.entries(DIRECTION_TYPES).map(([key, value]) => (<option key={key} value={value}>{value}</option>))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Motor</label>
                  <select value={newOrder.motorType || ''} onChange={(e) => handleMotorTypeChange(e.target.value)} disabled={isLoading}>
                    <option value="">Seçiniz</option>
                    {Object.entries(MOTOR_TYPES).map(([key, value]) => (<option key={key} value={value}>{value}</option>))}
                  </select>
                </div>
                {newOrder.motorType && (
                  <>
                    <div className="form-group">
                      <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        Motor Ücreti ({adminSettings[`${selectedProductType}MotorCurrency`] || 'USD'})
                        <i
                          className={`fas ${showMotorCost ? "fa-unlock" : "fa-lock"}`}
                          style={{ fontSize: "12px", cursor: "pointer", color: showMotorCost ? "#1a1a1a" : "#ccc", padding: "4px" }}
                          onClick={() => setShowMotorCost(!showMotorCost)}
                          title="Maliyet girişi için tıkla"
                        ></i>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={newOrder.motorPriceCurrency || ''}
                        onKeyDown={handleNumericKeyDown}
                        onChange={(e) => onFieldChange('motorPriceCurrency', e.target.value)}
                        disabled={isLoading}
                      />
                      {newOrder.motorPriceCurrency && (
                        <small style={{ display: 'block', marginTop: '5px', color: '#1a1a1a' }}>
                          ≈ {newOrder.motorPrice ? Number(newOrder.motorPrice).toFixed(2) : 0} ₺
                        </small>
                      )}
                    </div>

                    {showMotorCost && (
                      <div className="form-group animate-fadeIn">
                        <label style={{ color: "#1a1a1a" }}>
                          Motor Maliyeti ({adminSettings[`${selectedProductType}MotorCurrency`] || 'USD'})
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={newOrder.motorCostCurrency !== undefined && newOrder.motorCostCurrency !== null ? newOrder.motorCostCurrency : ''}
                          onKeyDown={handleNumericKeyDown}
                          onChange={(e) => onFieldChange("motorCostCurrency", e.target.value)}
                          disabled={isLoading}
                          placeholder="Gizli maliyet"
                        />
                        {newOrder.motorCostCurrency && (
                          <small style={{ display: 'block', marginTop: '5px', color: '#1a1a1a' }}>
                            ≈ {newOrder.motorCost ? Number(newOrder.motorCost).toFixed(2) : 0} ₺
                          </small>
                        )}
                      </div>
                    )}
                  </>
                )}
                <div className="form-group">
                  <label>&nbsp;</label>
                  <label style={{ display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', backgroundColor: '#f8fafc', cursor: 'pointer', margin: 0, transition: 'all 0.2s ease' }} onMouseOver={(e) => { e.currentTarget.style.borderColor = '#94a3b8'; e.currentTarget.style.backgroundColor = '#f1f5f9' }} onMouseOut={(e) => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.backgroundColor = '#f8fafc' }}>
                    <input type="checkbox" style={{ width: '16px', height: '16px', marginRight: '10px', cursor: 'pointer', accentColor: '#1a1a1a' }} checked={newOrder.hasZigil || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasZigil', checked); onFieldChange('zigilAmount', checked ? adminSettings.zigilPrice : 0); onFieldChange('zigilCostAmount', checked ? adminSettings.zigilCost : 0); }} />
                    <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Ahşap Zıgıl Sistemi</span>
                  </label>
                </div>
                <div className="form-group">
                  <label>&nbsp;</label>
                  <label style={{ display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', backgroundColor: '#f8fafc', cursor: 'pointer', margin: 0, transition: 'all 0.2s ease' }} onMouseOver={(e) => { e.currentTarget.style.borderColor = '#94a3b8'; e.currentTarget.style.backgroundColor = '#f1f5f9' }} onMouseOut={(e) => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.backgroundColor = '#f8fafc' }}>
                    <input type="checkbox" style={{ width: '16px', height: '16px', marginRight: '10px', cursor: 'pointer', accentColor: '#1a1a1a' }} checked={newOrder.hasReduktor || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasReduktor', checked); const base = Number(newOrder.baseUnitPrice) || 0; const extraKur = newOrder.hasKurdela ? Number(adminSettings.kurdelaPrice) : 0; const extraRed = checked ? Number(adminSettings.reduktorPrice) : 0; onFieldChange('unitPrice', base + extraRed + extraKur); const baseCost = Number(newOrder.baseUnitCost) || 0; const extraKurCost = newOrder.hasKurdela ? Number(adminSettings.kurdelaCost) : 0; const extraRedCost = checked ? Number(adminSettings.reduktorCost) : 0; if (newOrder.baseUnitCost !== undefined) onFieldChange('unitCost', baseCost + extraRedCost + extraKurCost); }} />
                    <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Redüktörlü Perde Farkı (m2)</span>
                  </label>
                </div>
                <div className="form-group">
                  <label>&nbsp;</label>
                  <label style={{ display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', backgroundColor: '#f8fafc', cursor: 'pointer', margin: 0, transition: 'all 0.2s ease' }} onMouseOver={(e) => { e.currentTarget.style.borderColor = '#94a3b8'; e.currentTarget.style.backgroundColor = '#f1f5f9' }} onMouseOut={(e) => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.backgroundColor = '#f8fafc' }}>
                    <input type="checkbox" style={{ width: '16px', height: '16px', marginRight: '10px', cursor: 'pointer', accentColor: '#1a1a1a' }} checked={newOrder.hasKurdela || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasKurdela', checked); const base = Number(newOrder.baseUnitPrice) || 0; const extraRed = newOrder.hasReduktor ? Number(adminSettings.reduktorPrice) : 0; const extraKur = checked ? Number(adminSettings.kurdelaPrice) : 0; onFieldChange('unitPrice', base + extraRed + extraKur); const baseCost = Number(newOrder.baseUnitCost) || 0; const extraRedCost = newOrder.hasReduktor ? Number(adminSettings.reduktorCost) : 0; const extraKurCost = checked ? Number(adminSettings.kurdelaCost) : 0; if (newOrder.baseUnitCost !== undefined) onFieldChange('unitCost', baseCost + extraRedCost + extraKurCost); }} />
                    <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Kurdelalı Perde Farkı (m2)</span>
                  </label>
                </div>
                <div className="form-group">
                  <label>Birim Fiyat (₺) *</label>
                  <input type="number" step="0.01" min="0.01" onKeyDown={handleNumericKeyDown} value={newOrder.unitPrice} onChange={(e) => { onFieldChange('unitPrice', e.target.value); onFieldChange('baseUnitPrice', e.target.value); }} required disabled={isLoading} />
                </div>
                <div className="form-group">
                  <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>Montaj (₺)<i className={`fas ${showMountingCost ? "fa-unlock" : "fa-lock"}`} style={{ fontSize: "12px", cursor: "pointer", color: showMountingCost ? "#1a1a1a" : "#ccc", padding: "4px" }} onClick={() => setShowMountingCost(!showMountingCost)} title="Maliyet girişi için tıkla"></i></label>
                  <input type="number" step="0.01" value={newOrder.mounting} onChange={(e) => onFieldChange('mounting', e.target.value)} disabled={isLoading} />
                </div>

                {showMountingCost && (
                  <div className="form-group animate-fadeIn">
                    <label style={{ color: "#1a1a1a" }}>Montaj Maliyeti (₺)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newOrder.mountingCost}
                      onKeyDown={handleNumericKeyDown}
                      onChange={(e) => onFieldChange("mountingCost", e.target.value)}
                      disabled={isLoading}
                      placeholder="Gizli maliyet"
                    />
                  </div>
                )}
                <div className="form-group">
                  <label>Adet *</label>
                  <input type="number" step="1" min="1" onKeyDown={handleNumericKeyDown} value={newOrder.quantity || ''} onChange={(e) => onFieldChange('quantity', e.target.value)} required disabled={isLoading} />
                </div>
                <div className="form-group">
                  <label>İskonto (%)</label>
                  <input type="number" step="0.01" min="0" max="100" onKeyDown={handleNumericKeyDown} value={newOrder.discount} onChange={(e) => onFieldChange('discount', e.target.value)} disabled={isLoading} />
                </div>
                {newOrder.totalPrice && (
                  <div className="form-group">
                    <label>Toplam Tutar (₺)</label>
                    <input type="text" value={formatPrice(newOrder.totalPrice)} readOnly className="total-price" />
                  </div>
                )}
                {newOrder.discountedPrice && parseFloat(newOrder.discount) > 0 && (
                  <div className="form-group">
                    <label>İndirimli Tutar (₺)</label>
                    <input type="text" value={formatPrice(newOrder.discountedPrice)} readOnly className="discounted-price" />
                  </div>
                )}
              </>
            )}

            {/* Metal Jaluzi için özel alanlar */}
            {selectedProductType === 'metalJaluzi' && (
              <>
                <div className="form-group">
                  <label>Oda Adı *</label>
                  <input type="text" value={newOrder.roomName} onChange={(e) => onFieldChange('roomName', e.target.value)} required disabled={isLoading} />
                </div>
                <div className="form-group">
                  <label>Ürün Adı *</label>
                  <CreatableSelect
                    options={productOptions}
                    value={
                      productOptions.find(o => o.value === (productItems.find(p => p.name === newOrder.productName)?.id)) ||
                      (newOrder.productName ? { value: newOrder.productName, label: newOrder.productName } : null)
                    }
                    onChange={(option) => {
                      if (!option) {
                        onFieldChange('productName', '');
                        onFieldChange('unitPrice', '');
                      } else if (option.__isNew__) {
                        onFieldChange('productName', option.value);
                        onFieldChange('unitPrice', '');
                      } else {
                        handleProductSelect(option.value);
                      }
                    }}
                    isDisabled={isLoading}
                    isClearable
                    isSearchable
                    placeholder="Ürün seçin veya yazın..."
                    noOptionsMessage={() => "Ürün bulunamadı"}
                    formatCreateLabel={(inputValue) => `"${inputValue}" olarak ekle`}
                    styles={customSelectStyles}
                  />
                </div>
                <div className="form-group">
                  <label>Ürün Varyantı</label>
                  <input
                    type="text"
                    value={newOrder.productVariant || ''}
                    onChange={(e) => onFieldChange('productVariant', e.target.value)}
                    disabled={isLoading}
                    placeholder="Renk, desen vb. giriniz..."
                  />
                </div>
                <div className="form-group">
                  <label>En (cm) *</label>
                  <input type="number" step="0.01" onKeyDown={handleNumericKeyDown} value={newOrder.width} onChange={(e) => onFieldChange('width', e.target.value)} required disabled={isLoading} />
                </div>
                <div className="form-group">
                  <label>Boy (cm) *</label>
                  <input type="number" step="0.01" onKeyDown={handleNumericKeyDown} value={newOrder.height} onChange={(e) => onFieldChange('height', e.target.value)} required disabled={isLoading} />
                </div>
                {newOrder.usedM2 && (
                  <div className="form-group">
                    <label>Kullanılan Mt²</label>
                    <input type="text" value={newOrder.usedM2} readOnly className="readonly-field" />
                  </div>
                )}
                <div className="form-group">
                  <label>Yön *</label>
                  <select value={newOrder.direction || ''} onChange={(e) => onFieldChange('direction', e.target.value)} required disabled={isLoading}>
                    <option value="">Seçiniz</option>
                    {Object.entries(DIRECTION_TYPES).map(([key, value]) => (<option key={key} value={value}>{value}</option>))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Motor</label>
                  <select value={newOrder.motorType || ''} onChange={(e) => handleMotorTypeChange(e.target.value)} disabled={isLoading}>
                    <option value="">Seçiniz</option>
                    {Object.entries(MOTOR_TYPES).map(([key, value]) => (<option key={key} value={value}>{value}</option>))}
                  </select>
                </div>
                {newOrder.motorType && (
                  <>
                    <div className="form-group">
                      <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        Motor Ücreti ({adminSettings[`${selectedProductType}MotorCurrency`] || 'USD'})
                        <i
                          className={`fas ${showMotorCost ? "fa-unlock" : "fa-lock"}`}
                          style={{ fontSize: "12px", cursor: "pointer", color: showMotorCost ? "#1a1a1a" : "#ccc", padding: "4px" }}
                          onClick={() => setShowMotorCost(!showMotorCost)}
                          title="Maliyet girişi için tıkla"
                        ></i>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={newOrder.motorPriceCurrency || ''}
                        onKeyDown={handleNumericKeyDown}
                        onChange={(e) => onFieldChange('motorPriceCurrency', e.target.value)}
                        disabled={isLoading}
                      />
                      {newOrder.motorPriceCurrency && (
                        <small style={{ display: 'block', marginTop: '5px', color: '#1a1a1a' }}>
                          ≈ {newOrder.motorPrice ? Number(newOrder.motorPrice).toFixed(2) : 0} ₺
                        </small>
                      )}
                    </div>

                    {showMotorCost && (
                      <div className="form-group animate-fadeIn">
                        <label style={{ color: "#1a1a1a" }}>
                          Motor Maliyeti ({adminSettings[`${selectedProductType}MotorCurrency`] || 'USD'})
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={newOrder.motorCostCurrency !== undefined && newOrder.motorCostCurrency !== null ? newOrder.motorCostCurrency : ''}
                          onKeyDown={handleNumericKeyDown}
                          onChange={(e) => onFieldChange("motorCostCurrency", e.target.value)}
                          disabled={isLoading}
                          placeholder="Gizli maliyet"
                        />
                        {newOrder.motorCostCurrency && (
                          <small style={{ display: 'block', marginTop: '5px', color: '#1a1a1a' }}>
                            ≈ {newOrder.motorCost ? Number(newOrder.motorCost).toFixed(2) : 0} ₺
                          </small>
                        )}
                      </div>
                    )}
                  </>
                )}
                <div className="form-group">
                  <label>&nbsp;</label>
                  <label style={{ display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', backgroundColor: '#f8fafc', cursor: 'pointer', margin: 0, transition: 'all 0.2s ease' }} onMouseOver={(e) => { e.currentTarget.style.borderColor = '#94a3b8'; e.currentTarget.style.backgroundColor = '#f1f5f9' }} onMouseOut={(e) => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.backgroundColor = '#f8fafc' }}>
                    <input type="checkbox" style={{ width: '16px', height: '16px', marginRight: '10px', cursor: 'pointer', accentColor: '#1a1a1a' }} checked={newOrder.hasMetalZigil || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasMetalZigil', checked); onFieldChange('metalZigilAmount', checked ? adminSettings.metalZigilPrice : 0); onFieldChange('metalZigilCostAmount', checked ? adminSettings.metalZigilCost : 0); }} />
                    <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Zıgıl Sistem Telli Takım</span>
                  </label>
                </div>
                <div className="form-group">
                  <label>&nbsp;</label>
                  <label style={{ display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', backgroundColor: '#f8fafc', cursor: 'pointer', margin: 0, transition: 'all 0.2s ease' }} onMouseOver={(e) => { e.currentTarget.style.borderColor = '#94a3b8'; e.currentTarget.style.backgroundColor = '#f1f5f9' }} onMouseOut={(e) => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.backgroundColor = '#f8fafc' }}>
                    <input type="checkbox" style={{ width: '16px', height: '16px', marginRight: '10px', cursor: 'pointer', accentColor: '#1a1a1a' }} checked={newOrder.hasMetalReduktor || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasMetalReduktor', checked); const base = Number(newOrder.baseUnitPrice) || 0; const extraRed = checked ? Number(adminSettings.metalReduktorPrice) : 0; onFieldChange('unitPrice', base + extraRed); const baseCost = Number(newOrder.baseUnitCost) || 0; const extraRedCost = checked ? Number(adminSettings.metalReduktorCost) : 0; if (newOrder.baseUnitCost !== undefined) onFieldChange('unitCost', baseCost + extraRedCost); }} />
                    <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Redüktörlü Perde Farkı (m2)</span>
                  </label>
                </div>
                <div className="form-group">
                  <label>&nbsp;</label>
                  <label style={{ display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', backgroundColor: '#f8fafc', cursor: 'pointer', margin: 0, transition: 'all 0.2s ease' }} onMouseOver={(e) => { e.currentTarget.style.borderColor = '#94a3b8'; e.currentTarget.style.backgroundColor = '#f1f5f9' }} onMouseOut={(e) => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.backgroundColor = '#f8fafc' }}>
                    <input type="checkbox" style={{ width: '16px', height: '16px', marginRight: '10px', cursor: 'pointer', accentColor: '#1a1a1a' }} checked={newOrder.hasIkiCamArasi || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasIkiCamArasi', checked); onFieldChange('ikiCamArasiAmount', checked ? adminSettings.ikiCamArasiPrice : 0); onFieldChange('ikiCamArasiCostAmount', checked ? adminSettings.ikiCamArasiCost : 0); }} />
                    <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>İki Cam Arası Buton Sistem Takım</span>
                  </label>
                </div>
                <div className="form-group">
                  <label>Birim Fiyat (₺) *</label>
                  <input type="number" step="0.01" min="0.01" onKeyDown={handleNumericKeyDown} value={newOrder.unitPrice} onChange={(e) => { onFieldChange('unitPrice', e.target.value); onFieldChange('baseUnitPrice', e.target.value); }} required disabled={isLoading} />
                </div>
                <div className="form-group">
                  <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>Montaj (₺)<i className={`fas ${showMountingCost ? "fa-unlock" : "fa-lock"}`} style={{ fontSize: "12px", cursor: "pointer", color: showMountingCost ? "#1a1a1a" : "#ccc", padding: "4px" }} onClick={() => setShowMountingCost(!showMountingCost)} title="Maliyet girişi için tıkla"></i></label>
                  <input type="number" step="0.01" value={newOrder.mounting} onChange={(e) => onFieldChange('mounting', e.target.value)} disabled={isLoading} />
                </div>

                {showMountingCost && (
                  <div className="form-group animate-fadeIn">
                    <label style={{ color: "#1a1a1a" }}>Montaj Maliyeti (₺)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newOrder.mountingCost}
                      onKeyDown={handleNumericKeyDown}
                      onChange={(e) => onFieldChange("mountingCost", e.target.value)}
                      disabled={isLoading}
                      placeholder="Gizli maliyet"
                    />
                  </div>
                )}
                <div className="form-group">
                  <label>Adet *</label>
                  <input type="number" step="1" min="1" onKeyDown={handleNumericKeyDown} value={newOrder.quantity || ''} onChange={(e) => onFieldChange('quantity', e.target.value)} required disabled={isLoading} />
                </div>
                <div className="form-group">
                  <label>İskonto (%)</label>
                  <input type="number" step="0.01" min="0" max="100" onKeyDown={handleNumericKeyDown} value={newOrder.discount} onChange={(e) => onFieldChange('discount', e.target.value)} disabled={isLoading} />
                </div>
                {newOrder.totalPrice && (
                  <div className="form-group">
                    <label>Toplam Tutar (₺)</label>
                    <input type="text" value={formatPrice(newOrder.totalPrice)} readOnly className="total-price" />
                  </div>
                )}
                {newOrder.discountedPrice && parseFloat(newOrder.discount) > 0 && (
                  <div className="form-group">
                    <label>İndirimli Tutar (₺)</label>
                    <input type="text" value={formatPrice(newOrder.discountedPrice)} readOnly className="discounted-price" />
                  </div>
                )}
              </>
            )}

            {/* Dikey PVC ve Dikey Stor için özel alanlar */}
            {(selectedProductType === 'dikeyPvc' || selectedProductType === 'dikeyStor') && (
              <>
                <div className="form-group">
                  <label>Oda Adı *</label>
                  <input
                    type="text"
                    value={newOrder.roomName}
                    onChange={(e) => onFieldChange('roomName', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>Ürün Adı *</label>
                  <CreatableSelect
                    options={productOptions}
                    value={
                      productOptions.find(o => o.value === (productItems.find(p => p.name === newOrder.productName)?.id)) ||
                      (newOrder.productName ? { value: newOrder.productName, label: newOrder.productName } : null)
                    }
                    onChange={(option) => {
                      if (!option) {
                        onFieldChange('productName', '');
                        onFieldChange('unitPrice', '');
                      } else if (option.__isNew__) {
                        onFieldChange('productName', option.value);
                        onFieldChange('unitPrice', '');
                      } else {
                        handleProductSelect(option.value);
                      }
                    }}
                    isDisabled={isLoading}
                    isClearable
                    isSearchable
                    placeholder="Ürün seçin veya yazın..."
                    noOptionsMessage={() => "Ürün bulunamadı"}
                    formatCreateLabel={(inputValue) => `"${inputValue}" olarak ekle`}
                    styles={customSelectStyles}
                  />
                </div>

                <div className="form-group">
                  <label>Ürün Varyantı</label>
                  <input
                    type="text"
                    value={newOrder.productVariant || ''}
                    onChange={(e) => onFieldChange('productVariant', e.target.value)}
                    disabled={isLoading}
                    placeholder="Örn: Siyah Kasa, Ekstra Yumuşak vb."
                  />
                </div>

                <div className="form-group">
                  <label>En (cm) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newOrder.width}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('width', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>Boy (cm) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newOrder.height}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('height', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>Yön *</label>
                  <select
                    value={newOrder.direction || ''}
                    onChange={(e) => onFieldChange('direction', e.target.value)}
                    required
                    disabled={isLoading}
                  >
                    <option value="">Seçiniz</option>
                    {Object.entries(DIKEY_DIRECTION_TYPES).map(([key, value]) => (
                      <option key={key} value={value}>{value}</option>
                    ))}
                  </select>
                </div>

                {newOrder.usedM2 && (
                  <div className="form-group">
                    <label>Kullanılan Mt²</label>
                    <input
                      type="text"
                      value={newOrder.usedM2}
                      readOnly
                      className="readonly-field"
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Birim Fiyat (₺) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={newOrder.unitPrice}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('unitPrice', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>Motor</label>
                  <select
                    value={newOrder.motorType || ''}
                    onChange={(e) => handleMotorTypeChange(e.target.value)}
                    disabled={isLoading}
                  >
                    <option value="">Seçiniz</option>
                    {Object.entries(MOTOR_TYPES).map(([key, value]) => (
                      <option key={key} value={value}>{value}</option>
                    ))}
                  </select>
                </div>

                {newOrder.motorType && (
                  <>
                    <div className="form-group">
                      <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        Motor Ücreti ({adminSettings[`${selectedProductType}MotorCurrency`] || 'USD'})
                        <i
                          className={`fas ${showMotorCost ? "fa-unlock" : "fa-lock"}`}
                          style={{ fontSize: "12px", cursor: "pointer", color: showMotorCost ? "#1a1a1a" : "#ccc", padding: "4px" }}
                          onClick={() => setShowMotorCost(!showMotorCost)}
                          title="Maliyet girişi için tıkla"
                        ></i>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={newOrder.motorPriceCurrency || ''}
                        onKeyDown={handleNumericKeyDown}
                        onChange={(e) => onFieldChange('motorPriceCurrency', e.target.value)}
                        disabled={isLoading}
                      />
                      {newOrder.motorPriceCurrency && (
                        <small style={{ display: 'block', marginTop: '5px', color: '#1a1a1a' }}>
                          ≈ {newOrder.motorPrice ? Number(newOrder.motorPrice).toFixed(2) : 0} ₺
                        </small>
                      )}
                    </div>

                    {showMotorCost && (
                      <div className="form-group animate-fadeIn">
                        <label style={{ color: "#1a1a1a" }}>
                          Motor Maliyeti ({adminSettings[`${selectedProductType}MotorCurrency`] || 'USD'})
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={newOrder.motorCostCurrency !== undefined && newOrder.motorCostCurrency !== null ? newOrder.motorCostCurrency : ''}
                          onKeyDown={handleNumericKeyDown}
                          onChange={(e) => onFieldChange("motorCostCurrency", e.target.value)}
                          disabled={isLoading}
                          placeholder="Gizli maliyet"
                        />
                        {newOrder.motorCostCurrency && (
                          <small style={{ display: 'block', marginTop: '5px', color: '#1a1a1a' }}>
                            ≈ {newOrder.motorCost ? Number(newOrder.motorCost).toFixed(2) : 0} ₺
                          </small>
                        )}
                      </div>
                    )}
                  </>
                )}

                <div className="form-group">
                  <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>Montaj (₺)<i className={`fas ${showMountingCost ? "fa-unlock" : "fa-lock"}`} style={{ fontSize: "12px", cursor: "pointer", color: showMountingCost ? "#1a1a1a" : "#ccc", padding: "4px" }} onClick={() => setShowMountingCost(!showMountingCost)} title="Maliyet girişi için tıkla"></i></label>
                  <input
                    type="number"
                    step="0.01"
                    value={newOrder.mounting}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('mounting', e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                {showMountingCost && (
                  <div className="form-group animate-fadeIn">
                    <label style={{ color: "#1a1a1a" }}>Montaj Maliyeti (₺)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newOrder.mountingCost}
                      onKeyDown={handleNumericKeyDown}
                      onChange={(e) => onFieldChange("mountingCost", e.target.value)}
                      disabled={isLoading}
                      placeholder="Gizli maliyet"
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Adet *</label>
                  <input
                    type="number"
                    step="1"
                    min="1"
                    value={newOrder.quantity || ''}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('quantity', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>İskonto (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={newOrder.discount}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('discount', e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                {newOrder.totalPrice && (
                  <div className="form-group">
                    <label>Toplam Tutar (₺)</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.totalPrice)}
                      readOnly
                      className="total-price"
                    />
                  </div>
                )}

                {newOrder.discountedPrice && parseFloat(newOrder.discount) > 0 && (
                  <div className="form-group">
                    <label>İndirimli Tutar (₺)</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.discountedPrice)}
                      readOnly
                      className="discounted-price"
                    />
                  </div>
                )}
              </>
            )}

            {/* Dikey Perde için özel alanlar */}
            {selectedProductType === 'dikeyPerde' && (
              <>
                <div className="form-group">
                  <label>Oda Adı *</label>
                  <input
                    type="text"
                    value={newOrder.roomName}
                    onChange={(e) => onFieldChange('roomName', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>Ürün Adı *</label>
                  <CreatableSelect
                    options={productOptions}
                    value={
                      productOptions.find(o => o.value === (productItems.find(p => p.name === newOrder.productName)?.id)) ||
                      (newOrder.productName ? { value: newOrder.productName, label: newOrder.productName } : null)
                    }
                    onChange={(option) => {
                      if (!option) {
                        onFieldChange('productName', '');
                        onFieldChange('unitPrice', '');
                      } else if (option.__isNew__) {
                        onFieldChange('productName', option.value);
                        onFieldChange('unitPrice', '');
                      } else {
                        handleProductSelect(option.value);
                      }
                    }}
                    isDisabled={isLoading}
                    isClearable
                    isSearchable
                    placeholder="Ürün seçin veya yazın..."
                    noOptionsMessage={() => "Ürün bulunamadı"}
                    formatCreateLabel={(inputValue) => `"${inputValue}" olarak ekle`}
                    styles={customSelectStyles}
                  />
                </div>

                <div className="form-group">
                  <label>Ürün Varyantı</label>
                  <input
                    type="text"
                    value={newOrder.productVariant || ''}
                    onChange={(e) => onFieldChange('productVariant', e.target.value)}
                    disabled={isLoading}
                    placeholder="Örn: Siyah Kasa, Ekstra Yumuşak vb."
                  />
                </div>

                <div className="form-group">
                  <label>En (cm) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newOrder.width}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('width', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>Boy (cm) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newOrder.height}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('height', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>Yön *</label>
                  <select
                    value={newOrder.direction || ''}
                    onChange={(e) => onFieldChange('direction', e.target.value)}
                    required
                    disabled={isLoading}
                  >
                    <option value="">Seçiniz</option>
                    {Object.entries(DIKEY_DIRECTION_TYPES).map(([key, value]) => (
                      <option key={key} value={value}>{value}</option>
                    ))}
                  </select>
                </div>

                {newOrder.usedM2 && (
                  <div className="form-group">
                    <label>Kullanılan Mt²</label>
                    <input
                      type="text"
                      value={newOrder.usedM2}
                      readOnly
                      className="readonly-field"
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Birim Fiyat (₺) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={newOrder.unitPrice}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('unitPrice', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>&nbsp;</label>
                  <label
                    style={{
                      display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px',
                      border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%',
                      backgroundColor: !newOrder.unitPrice ? '#f1f5f9' : '#f8fafc',
                      cursor: !newOrder.unitPrice ? 'not-allowed' : 'pointer',
                      margin: 0, transition: 'all 0.2s ease',
                      opacity: !newOrder.unitPrice ? 0.6 : 1
                    }}
                    onMouseOver={(e) => {
                      if (newOrder.unitPrice) {
                        e.currentTarget.style.borderColor = '#94a3b8';
                        e.currentTarget.style.backgroundColor = '#f1f5f9';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (newOrder.unitPrice) {
                        e.currentTarget.style.borderColor = '#cbd5e1';
                        e.currentTarget.style.backgroundColor = '#f8fafc';
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      disabled={!newOrder.unitPrice || isLoading}
                      style={{
                        width: '16px', height: '16px', marginRight: '10px',
                        cursor: !newOrder.unitPrice ? 'not-allowed' : 'pointer',
                        accentColor: '#1a1a1a'
                      }}
                      checked={newOrder.hasDikeyFon || false}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        onFieldChange('hasDikeyFon', checked);
                        const fonPrice = Number(adminSettings.dikeyFonPrice) || 0;
                        const fonCost = Number(adminSettings.dikeyFonCost) || 0;
                        onFieldChange('dikeyFonAmount', checked ? fonPrice : 0);

                        const currentUnit = parseFloat(newOrder.unitPrice) || 0;
                        const newUnit = checked ? currentUnit + fonPrice : Math.max(0, currentUnit - fonPrice);
                        onFieldChange('unitPrice', newUnit.toFixed(2));

                        const currentCost = parseFloat(newOrder.unitCost) || 0;
                        const newCost = checked ? currentCost + fonCost : Math.max(0, currentCost - fonCost);
                        onFieldChange('unitCost', newCost.toFixed(2));

                        // Fon sayısı seçiliyse varsayılan olarak 1 atayalım
                        if (checked && !newOrder.dikeyFonCount) {
                          onFieldChange('dikeyFonCount', 1);
                        }
                      }}
                    />
                    <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Fon (+{adminSettings.dikeyFonPrice} ₺)</span>
                  </label>
                </div>

                {newOrder.hasDikeyFon && (
                  <>
                    <div className="form-group">
                      <label>Fon Adı *</label>
                      {adminSettings.dikeyFonList && adminSettings.dikeyFonList.length > 0 ? (
                        <Select
                          options={adminSettings.dikeyFonList.map(f => ({ value: f, label: f }))}
                          value={newOrder.dikeyFonName ? { value: newOrder.dikeyFonName, label: newOrder.dikeyFonName } : null}
                          onChange={(option) => onFieldChange('dikeyFonName', option ? option.value : '')}
                          isDisabled={isLoading}
                          isClearable
                          isSearchable
                          placeholder="Fon seçiniz..."
                          noOptionsMessage={() => "Fon bulunamadı"}
                          styles={customSelectStyles}
                        />
                      ) : (
                        <input
                          type="text"
                          value={newOrder.dikeyFonName || ''}
                          onChange={(e) => onFieldChange('dikeyFonName', e.target.value)}
                          disabled={isLoading}
                          placeholder="Fon adı girin (Fon ürünleri listesi boş)"
                        />
                      )}
                    </div>
                    <div className="form-group">
                      <label>Fon Sayısı</label>
                      <input
                        type="number"
                        step="1"
                        min="1"
                        value={newOrder.dikeyFonCount || ''}
                        onChange={(e) => {
                          let val = e.target.value;
                          if (val !== '' && parseInt(val) < 1) val = '1';
                          onFieldChange('dikeyFonCount', val);
                        }}
                        disabled={isLoading}
                      />
                    </div>
                  </>
                )}

                <div className="form-group">
                  <label>Motor</label>
                  <select
                    value={newOrder.motorType || ''}
                    onChange={(e) => handleMotorTypeChange(e.target.value)}
                    disabled={isLoading}
                  >
                    <option value="">Seçiniz</option>
                    {Object.entries(MOTOR_TYPES).map(([key, value]) => (
                      <option key={key} value={value}>{value}</option>
                    ))}
                  </select>
                </div>

                {newOrder.motorType && (
                  <>
                    <div className="form-group">
                      <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        Motor Ücreti ({adminSettings[`${selectedProductType}MotorCurrency`] || 'USD'})
                        <i
                          className={`fas ${showMotorCost ? "fa-unlock" : "fa-lock"}`}
                          style={{ fontSize: "12px", cursor: "pointer", color: showMotorCost ? "#1a1a1a" : "#ccc", padding: "4px" }}
                          onClick={() => setShowMotorCost(!showMotorCost)}
                          title="Maliyet girişi için tıkla"
                        ></i>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={newOrder.motorPriceCurrency || ''}
                        onKeyDown={handleNumericKeyDown}
                        onChange={(e) => onFieldChange('motorPriceCurrency', e.target.value)}
                        disabled={isLoading}
                      />
                      {newOrder.motorPriceCurrency && (
                        <small style={{ display: 'block', marginTop: '5px', color: '#1a1a1a' }}>
                          ≈ {newOrder.motorPrice ? Number(newOrder.motorPrice).toFixed(2) : 0} ₺
                        </small>
                      )}
                    </div>

                    {showMotorCost && (
                      <div className="form-group animate-fadeIn">
                        <label style={{ color: "#1a1a1a" }}>
                          Motor Maliyeti ({adminSettings[`${selectedProductType}MotorCurrency`] || 'USD'})
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={newOrder.motorCostCurrency !== undefined && newOrder.motorCostCurrency !== null ? newOrder.motorCostCurrency : ''}
                          onKeyDown={handleNumericKeyDown}
                          onChange={(e) => onFieldChange("motorCostCurrency", e.target.value)}
                          disabled={isLoading}
                          placeholder="Gizli maliyet"
                        />
                        {newOrder.motorCostCurrency && (
                          <small style={{ display: 'block', marginTop: '5px', color: '#1a1a1a' }}>
                            ≈ {newOrder.motorCost ? Number(newOrder.motorCost).toFixed(2) : 0} ₺
                          </small>
                        )}
                      </div>
                    )}
                  </>
                )}

                <div className="form-group">
                  <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>Montaj (₺)<i className={`fas ${showMountingCost ? "fa-unlock" : "fa-lock"}`} style={{ fontSize: "12px", cursor: "pointer", color: showMountingCost ? "#1a1a1a" : "#ccc", padding: "4px" }} onClick={() => setShowMountingCost(!showMountingCost)} title="Maliyet girişi için tıkla"></i></label>
                  <input
                    type="number"
                    step="0.01"
                    value={newOrder.mounting}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('mounting', e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                {showMountingCost && (
                  <div className="form-group animate-fadeIn">
                    <label style={{ color: "#1a1a1a" }}>Montaj Maliyeti (₺)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newOrder.mountingCost}
                      onKeyDown={handleNumericKeyDown}
                      onChange={(e) => onFieldChange("mountingCost", e.target.value)}
                      disabled={isLoading}
                      placeholder="Gizli maliyet"
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Adet *</label>
                  <input
                    type="number"
                    step="1"
                    min="1"
                    value={newOrder.quantity || ''}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('quantity', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>İskonto (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={newOrder.discount}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('discount', e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                {newOrder.totalPrice && (
                  <div className="form-group">
                    <label>Toplam Tutar (₺)</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.totalPrice)}
                      readOnly
                      className="total-price"
                    />
                  </div>
                )}

                {newOrder.discountedPrice && parseFloat(newOrder.discount) > 0 && (
                  <div className="form-group">
                    <label>İndirimli Tutar (₺)</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.discountedPrice)}
                      readOnly
                      className="discounted-price"
                    />
                  </div>
                )}
              </>
            )}

            {/* Plise için özel alanlar - Yeni Batch Sistemi */}
            {selectedProductType === 'plise' && (
              <>
                {/* Başlık Alanları */}
                <div className="form-group">
                  <label>Oda Adı *</label>
                  <input
                    type="text"
                    value={newOrder.roomName}
                    onChange={(e) => onFieldChange('roomName', e.target.value)}
                    required
                    disabled={isLoading}
                    placeholder="Örn: Salon"
                  />
                </div>

                <div className="form-group">
                  <label>1. Ürün Adı *</label>
                  <CreatableSelect
                    options={productOptions}
                    value={
                      productOptions.find(o => o.label === newOrder.pliseProduct1Name) ||
                      (newOrder.pliseProduct1Name ? { value: newOrder.pliseProduct1Name, label: newOrder.pliseProduct1Name } : null)
                    }
                    onChange={(option) => {
                      if (!option) {
                        onFieldChange('pliseProduct1Name', '');
                        onFieldChange('pliseProduct1Price', '');
                      } else if (option.__isNew__) {
                        onFieldChange('pliseProduct1Name', option.value);
                        onFieldChange('pliseProduct1Price', '');
                      } else {
                        handlePliseProduct1Select(option.value);
                      }
                    }}
                    isDisabled={isLoading}
                    isClearable
                    isSearchable
                    placeholder="Ürün seçin veya yazın..."
                    noOptionsMessage={() => "Ürün bulunamadı"}
                    formatCreateLabel={(inputValue) => `"${inputValue}" olarak ekle`}
                    styles={customSelectStyles}
                  />
                </div>

                <div className="form-group">
                  <label>1. Ürün Fiyat (₺) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={parseInt(newOrder.pliseProduct1Price) === 0 ? '' : newOrder.pliseProduct1Price}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('pliseProduct1Price', e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>1. Ürün Kumaş Varyantı</label>
                  <input
                    type="text"
                    value={newOrder.pliseProduct1Variation || ''}
                    onChange={(e) => onFieldChange('pliseProduct1Variation', e.target.value)}
                    disabled={isLoading}
                    placeholder="Renk/Desen"
                  />
                </div>

                <div className="form-group">
                  <label>Kasa Varyantı</label>
                  <input
                    type="text"
                    value={newOrder.pliseCaseVariation || ''}
                    onChange={(e) => onFieldChange('pliseCaseVariation', e.target.value)}
                    disabled={isLoading}
                    placeholder="Örn: Antrasit"
                  />
                </div>

                <div className="form-group">
                  <label>2. Ürün Adı</label>
                  <CreatableSelect
                    options={productOptions}
                    value={
                      productOptions.find(o => o.label === newOrder.pliseProduct2Name) ||
                      (newOrder.pliseProduct2Name ? { value: newOrder.pliseProduct2Name, label: newOrder.pliseProduct2Name } : null)
                    }
                    onChange={(option) => {
                      if (!option) {
                        onFieldChange('pliseProduct2Name', '');
                        onFieldChange('pliseProduct2Price', '');
                      } else if (option.__isNew__) {
                        onFieldChange('pliseProduct2Name', option.value);
                        onFieldChange('pliseProduct2Price', '');
                      } else {
                        handlePliseProduct2Select(option.value);
                      }
                    }}
                    isDisabled={isLoading}
                    isClearable
                    isSearchable
                    placeholder="Ürün seçin veya yazın..."
                    noOptionsMessage={() => "Ürün bulunamadı"}
                    formatCreateLabel={(inputValue) => `"${inputValue}" olarak ekle`}
                    styles={customSelectStyles}
                  />
                </div>

                <div className="form-group">
                  <label>2. Ürün Fiyat (₺)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={parseInt(newOrder.pliseProduct2Price) === 0 ? '' : newOrder.pliseProduct2Price}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('pliseProduct2Price', e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>2. Ürün Kumaş Varyantı</label>
                  <input
                    type="text"
                    value={newOrder.pliseProduct2Variation || ''}
                    onChange={(e) => onFieldChange('pliseProduct2Variation', e.target.value)}
                    disabled={isLoading}
                    placeholder="Renk/Desen"
                  />
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Montaj Toplam (₺)
                    <i
                      className={`fas ${showMountingCost ? 'fa-unlock' : 'fa-lock'}`}
                      style={{
                        fontSize: '12px',
                        cursor: 'pointer',
                        color: showMountingCost ? '#1a1a1a' : '#ccc',
                        padding: '4px'
                      }}
                      onClick={() => setShowMountingCost(!showMountingCost)}
                      title="Maliyet girişi için tıkla"
                    ></i>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={newOrder.mounting}
                    onChange={(e) => onFieldChange('mounting', e.target.value)}
                    placeholder="0.00"
                    disabled={isLoading}
                  />
                </div>

                {showMountingCost && (
                  <div className="form-group animate-fadeIn">
                    <label style={{ color: '#1a1a1a' }}>Montaj Maliyeti (₺)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newOrder.mountingCost}
                      onKeyDown={handleNumericKeyDown}
                      onChange={(e) => onFieldChange('mountingCost', e.target.value)}
                      disabled={isLoading}
                      placeholder="Gizli maliyet"
                    />
                  </div>
                )}


                {/* Dinamik Ölçü Listesi Section */}
                <div style={{ gridColumn: '1 / -1', padding: '15px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', marginTop: '20px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h4 style={{ margin: 0, fontSize: '16px', color: '#0f172a', fontWeight: '700', display: 'flex', alignItems: 'center' }}>
                      <i className="fas fa-layer-group" style={{ marginRight: '10px', color: '#1a1a1a' }}></i>
                      Plise Ölçü ve Adet Listesi
                    </h4>
                    <button
                      type="button"
                      onClick={() => {
                        const newList = [...(newOrder.pliseItems || []), { id: Date.now(), width: '', height: '', usedM2: '1.00', quantity: '1', multisistem: false, rowTotal: '0.00' }];
                        onFieldChange('pliseItems', newList);
                      }}
                      style={{ padding: '8px 16px', backgroundColor: '#0f172a', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '8px' }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#1e293b'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#0f172a'}
                    >
                      <i className="fas fa-plus"></i>
                      Yeni Ölçü Ekle
                    </button>
                  </div>

                  <div className="plise-batch-wrapper">
                    <table className="plise-batch-table">
                      <thead>
                        <tr>
                          <th>En (cm)</th>
                          <th>Boy (cm)</th>
                          <th>m²</th>
                          <th>Adet</th>
                          <th style={{ textAlign: 'center' }}>Multisistem</th>
                          <th style={{ textAlign: 'right' }}>Tutar (₺)</th>
                          <th style={{ textAlign: 'center' }}>İşlemler</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.isArray(newOrder.pliseItems) && newOrder.pliseItems.length > 0 ? (
                          newOrder.pliseItems.map((item, idx) => (
                            <tr key={item.id || idx} className="plise-row">
                              <td data-label="En (cm)">
                                <input
                                  type="number"
                                  value={item.width}
                                  onChange={(e) => {
                                    const newList = [...newOrder.pliseItems];
                                    newList[idx].width = e.target.value;
                                    onFieldChange('pliseItems', newList);
                                  }}
                                  className="plise-input"
                                  placeholder="En"
                                />
                              </td>
                              <td data-label="Boy (cm)">
                                <input
                                  type="number"
                                  value={item.height}
                                  onChange={(e) => {
                                    const newList = [...newOrder.pliseItems];
                                    newList[idx].height = e.target.value;
                                    onFieldChange('pliseItems', newList);
                                  }}
                                  className="plise-input"
                                  placeholder="Boy"
                                />
                              </td>
                              <td data-label="m²">
                                <div className="plise-m2-badge">
                                  {item.usedM2 || '1.00'}
                                </div>
                              </td>
                              <td data-label="Adet">
                                <input
                                  type="number"
                                  min="1"
                                  value={item.quantity}
                                  onChange={(e) => {
                                    const newList = [...newOrder.pliseItems];
                                    newList[idx].quantity = e.target.value;
                                    onFieldChange('pliseItems', newList);
                                  }}
                                  className="plise-input qty-input"
                                />
                              </td>
                              <td data-label="Multisistem" style={{ textAlign: 'center' }}>
                                <input
                                  type="checkbox"
                                  checked={item.multisistem}
                                  onChange={(e) => {
                                    const newList = [...newOrder.pliseItems];
                                    newList[idx].multisistem = e.target.checked;
                                    onFieldChange('pliseItems', newList);
                                  }}
                                  className="plise-checkbox"
                                />
                              </td>
                              <td data-label="Tutar (₺)">
                                <div className="plise-row-total">
                                  {formatPrice(item.rowTotal || 0)} ₺
                                </div>
                              </td>
                              <td data-label="İşlemler">
                                <div className="plise-actions">
                                  <button
                                    type="button"
                                    className="plise-action-btn copy"
                                    title="Satırı Kopyala"
                                    onClick={() => {
                                      const rowToCopy = { ...newOrder.pliseItems[idx], id: Date.now() + Math.random() };
                                      const newList = [...newOrder.pliseItems];
                                      newList.splice(idx + 1, 0, rowToCopy);
                                      onFieldChange('pliseItems', newList);
                                    }}
                                  >
                                    <i className="fas fa-copy"></i>
                                  </button>
                                  <button
                                    type="button"
                                    className="plise-action-btn delete"
                                    title="Satırı Sil"
                                    onClick={() => {
                                      const newList = newOrder.pliseItems.filter((_, i) => i !== idx);
                                      onFieldChange('pliseItems', newList);
                                    }}
                                  >
                                    <i className="fas fa-trash"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="7" className="empty-message">
                              Henüz bir ölçü eklenmedi. Hızlıca eklemek için "Yeni Ölçü Ekle" butonunu kullanın.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Ek Ücret (₺)
                    <i
                      className={`fas ${showManualExtraCost ? 'fa-unlock' : 'fa-lock'}`}
                      style={{
                        fontSize: '12px',
                        cursor: 'pointer',
                        color: showManualExtraCost ? '#1a1a1a' : '#ccc',
                        padding: '4px'
                      }}
                      onClick={() => setShowManualExtraCost(!showManualExtraCost)}
                      title="Maliyet girişi için tıkla"
                    ></i>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newOrder.extraPrice}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('extraPrice', e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                {showManualExtraCost && (
                  <div className="form-group animate-fadeIn">
                    <label style={{ color: '#1a1a1a' }}>Ek Ücret Maliyeti (₺)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newOrder.manualExtraCost}
                      onKeyDown={handleNumericKeyDown}
                      onChange={(e) => onFieldChange('manualExtraCost', e.target.value)}
                      disabled={isLoading}
                      placeholder="Gizli maliyet"
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>İskonto (%)</label>
                  <input
                    type="number"
                    value={newOrder.discount}
                    onChange={(e) => onFieldChange('discount', e.target.value)}
                    min="0"
                    max="100"
                    placeholder="0"
                    disabled={isLoading}
                  />
                </div>

                {newOrder.totalPrice && (
                  <div className="form-group">
                    <label>Toplam Tutar (₺)</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.totalPrice)}
                      readOnly
                      className="total-price"
                    />
                  </div>
                )}

                {newOrder.discountedPrice && parseFloat(newOrder.discount) > 0 && (
                  <div className="form-group">
                    <label>İndirimli Tutar (₺)</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.discountedPrice)}
                      readOnly
                      className="discounted-price"
                    />
                  </div>
                )}
              </>
            )}

            {/* Katlamalı Perde için özel alanlar */}
            {selectedProductType === 'katlamaliPerde' && (
              <>
                <div className="form-group">
                  <label>Oda Adı *</label>
                  <input
                    type="text"
                    value={newOrder.roomName}
                    onChange={(e) => onFieldChange('roomName', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>Ürün Adı *</label>
                  <CreatableSelect
                    options={productOptions}
                    value={
                      productOptions.find(o => o.value === (productItems.find(p => p.name === newOrder.productName)?.id)) ||
                      (newOrder.productName ? { value: newOrder.productName, label: newOrder.productName } : null)
                    }
                    onChange={(option) => {
                      if (!option) {
                        onFieldChange('productName', '');
                        onFieldChange('unitPrice', '');
                      } else if (option.__isNew__) {
                        onFieldChange('productName', option.value);
                        onFieldChange('unitPrice', '');
                      } else {
                        handleProductSelect(option.value);
                      }
                    }}
                    isDisabled={isLoading}
                    isClearable
                    isSearchable
                    placeholder="Ürün seçin veya yazın..."
                    noOptionsMessage={() => "Ürün bulunamadı"}
                    formatCreateLabel={(inputValue) => `"${inputValue}" olarak ekle`}
                    styles={customSelectStyles}
                  />
                </div>

                <div className="form-group">
                  <label>Ürün Varyantı</label>
                  <input
                    type="text"
                    value={newOrder.productVariant || ''}
                    onChange={(e) => onFieldChange('productVariant', e.target.value)}
                    disabled={isLoading}
                    placeholder="Renk, desen vb. giriniz..."
                  />
                </div>

                <div className="form-group">
                  <label>En (cm) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newOrder.width}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('width', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>Boy (cm) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newOrder.height}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('height', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                {newOrder.usedM2 && (
                  <div className="form-group">
                    <label>Kullanılan Mt</label>
                    <input
                      type="text"
                      value={newOrder.usedM2}
                      readOnly
                      className="readonly-field"
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Kumaş (₺) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={newOrder.fabricPrice}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('fabricPrice', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>Mekanizma Tipi *</label>
                  {adminSettings.katlamaliMekanizmaList && adminSettings.katlamaliMekanizmaList.length > 0 ? (
                    <Select
                      options={adminSettings.katlamaliMekanizmaList.map(m => ({ value: m.type, label: `${m.type} (${m.price} ₺)` }))}
                      value={newOrder.mechanismType ? { value: newOrder.mechanismType, label: newOrder.mechanismType } : null}
                      onChange={(option) => handleKatlamaliMechanismSelect(option ? option.value : '')}
                      isDisabled={isLoading}
                      isClearable
                      isSearchable
                      placeholder="Seçiniz..."
                      styles={customSelectStyles}
                    />
                  ) : (
                    <input
                      type="text"
                      value={newOrder.mechanismType || ''}
                      onChange={(e) => onFieldChange('mechanismType', e.target.value)}
                      disabled={isLoading}
                      placeholder="Mekanizma tipi girin"
                    />
                  )}
                </div>

                <div className="form-group">
                  <label>Mekanizma (₺) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={newOrder.mechanismPrice}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('mechanismPrice', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                {/* Aksesuarlar Bölümü */}
                <div style={{ gridColumn: '1 / -1', padding: '15px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', marginTop: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h4 style={{ margin: 0, fontSize: '15px', color: '#1a1a1a' }}>
                      <i className="fas fa-tags" style={{ marginRight: '8px' }}></i>
                      Aksesuarlar
                    </h4>
                    <button
                      type="button"
                      onClick={handleAddAccessory}
                      style={{ padding: '6px 12px', backgroundColor: '#0f172a', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}
                    >
                      <i className="fas fa-plus" style={{ marginRight: '5px' }}></i>
                      Aksesuar Ekle
                    </button>
                  </div>

                  {Array.isArray(newOrder.accessories) && newOrder.accessories.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {newOrder.accessories.map((acc, idx) => (
                        <div key={acc.id} className="aksesuar-item-row">
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: '11px' }}>Aksesuar Adı</label>
                            <input
                              type="text"
                              value={acc.name}
                              onChange={(e) => handleAccessoryChange(acc.id, 'name', e.target.value)}
                              placeholder="Fisto, Boncuk vb."
                              style={{ padding: '6px' }}
                            />
                          </div>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              Fiyat (₺)
                              <i
                                className={`fas ${showAccessoryCost ? 'fa-unlock' : 'fa-lock'}`}
                                style={{ fontSize: '10px', cursor: 'pointer', color: showAccessoryCost ? '#1a1a1a' : '#ccc' }}
                                onClick={() => setShowAccessoryCost(!showAccessoryCost)}
                                title="Maliyet girişi için tıkla"
                              ></i>
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={acc.price}
                              onKeyDown={handleNumericKeyDown}
                              onChange={(e) => handleAccessoryChange(acc.id, 'price', e.target.value)}
                              style={{ padding: '6px' }}
                            />
                            {showAccessoryCost && (
                              <div style={{ marginTop: '5px' }}>
                                <label style={{ fontSize: '10px', color: '#1a1a1a', display: 'block' }}>Gizli Maliyet (₺)</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={acc.cost || '0'}
                                  onKeyDown={handleNumericKeyDown}
                                  onChange={(e) => handleAccessoryChange(acc.id, 'cost', e.target.value)}
                                  style={{ padding: '4px', fontSize: '11px', border: '1px solid #1a1a1a' }}
                                />
                              </div>
                            )}
                          </div>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: '11px' }}>Adet</label>
                            <input
                              type="number"
                              step="1"
                              min="1"
                              value={acc.quantity}
                              onKeyDown={handleNumericKeyDown}
                              onChange={(e) => handleAccessoryChange(acc.id, 'quantity', e.target.value)}
                              style={{ padding: '6px' }}
                            />
                          </div>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: '11px' }}>Tutar (₺)</label>
                            <input
                              type="text"
                              value={acc.total}
                              readOnly
                              style={{ padding: '6px', backgroundColor: '#f1f5f9' }}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveAccessory(acc.id)}
                            style={{ padding: '8px', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ fontSize: '13px', color: '#64748b', textAlign: 'center', margin: '10px 0' }}>Henüz aksesuar eklenmemiş.</p>
                  )}
                </div>

                {/* Motor Bölümü */}
                <div className="form-group">
                  <label>Motor</label>
                  <select
                    value={newOrder.motorType || ''}
                    onChange={(e) => handleMotorTypeChange(e.target.value)}
                    disabled={isLoading}
                  >
                    <option value="">Seçiniz</option>
                    {Object.entries(MOTOR_TYPES).map(([key, value]) => (
                      <option key={key} value={value}>{value}</option>
                    ))}
                  </select>
                </div>

                {newOrder.motorType && (
                  <>
                    <div className="form-group">
                      <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        Motor Ücreti ({adminSettings[`${selectedProductType}MotorCurrency`] || 'USD'})
                        <i
                          className={`fas ${showMotorCost ? "fa-unlock" : "fa-lock"}`}
                          style={{ fontSize: "12px", cursor: "pointer", color: showMotorCost ? "#1a1a1a" : "#ccc", padding: "4px" }}
                          onClick={() => setShowMotorCost(!showMotorCost)}
                          title="Maliyet girişi için tıkla"
                        ></i>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={newOrder.motorPriceCurrency || ''}
                        onKeyDown={handleNumericKeyDown}
                        onChange={(e) => onFieldChange('motorPriceCurrency', e.target.value)}
                        disabled={isLoading}
                      />
                      {newOrder.motorPriceCurrency && (
                        <small style={{ display: 'block', marginTop: '5px', color: '#1a1a1a' }}>
                          ≈ {newOrder.motorPrice ? Number(newOrder.motorPrice).toFixed(2) : 0} ₺
                        </small>
                      )}
                    </div>

                    {showMotorCost && (
                      <div className="form-group animate-fadeIn">
                        <label style={{ color: "#1a1a1a" }}>
                          Motor Maliyeti ({adminSettings[`${selectedProductType}MotorCurrency`] || 'USD'})
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={newOrder.motorCostCurrency !== undefined && newOrder.motorCostCurrency !== null ? newOrder.motorCostCurrency : ''}
                          onKeyDown={handleNumericKeyDown}
                          onChange={(e) => onFieldChange("motorCostCurrency", e.target.value)}
                          disabled={isLoading}
                          placeholder="Gizli maliyet"
                        />
                        {newOrder.motorCostCurrency && (
                          <small style={{ display: 'block', marginTop: '5px', color: '#1a1a1a' }}>
                            ≈ {newOrder.motorCost ? Number(newOrder.motorCost).toFixed(2) : 0} ₺
                          </small>
                        )}
                      </div>
                    )}
                  </>
                )}

                <div className="form-group">
                  <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>Montaj (₺)<i className={`fas ${showMountingCost ? "fa-unlock" : "fa-lock"}`} style={{ fontSize: "12px", cursor: "pointer", color: showMountingCost ? "#1a1a1a" : "#ccc", padding: "4px" }} onClick={() => setShowMountingCost(!showMountingCost)} title="Maliyet girişi için tıkla"></i></label>
                  <input
                    type="number"
                    step="0.01"
                    value={newOrder.mounting}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('mounting', e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                {showMountingCost && (
                  <div className="form-group animate-fadeIn">
                    <label style={{ color: "#1a1a1a" }}>Montaj Maliyeti (₺)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newOrder.mountingCost}
                      onKeyDown={handleNumericKeyDown}
                      onChange={(e) => onFieldChange("mountingCost", e.target.value)}
                      disabled={isLoading}
                      placeholder="Gizli maliyet"
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Adet *</label>
                  <input
                    type="number"
                    step="1"
                    min="1"
                    value={newOrder.quantity || ''}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('quantity', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>İskonto (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={newOrder.discount}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('discount', e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                {newOrder.totalPrice && (
                  <div className="form-group">
                    <label>Toplam Tutar (₺)</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.totalPrice)}
                      readOnly
                      className="total-price"
                    />
                  </div>
                )}

                {newOrder.discountedPrice && parseFloat(newOrder.discount) > 0 && (
                  <div className="form-group">
                    <label>İndirimli Tutar (₺)</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.discountedPrice)}
                      readOnly
                      className="discounted-price"
                    />
                  </div>
                )}
              </>
            )}

            {/* Aksesuar için özel alanlar */}
            {selectedProductType === 'aksesuar' && (
              <>
                <div className="form-group">
                  <label>Oda Adı *</label>
                  <input
                    type="text"
                    value={newOrder.roomName}
                    onChange={(e) => onFieldChange('roomName', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>Ürün Adı *</label>
                  <CreatableSelect
                    options={productOptions}
                    value={
                      productOptions.find(o => o.value === (productItems.find(p => p.name === newOrder.productName)?.id)) ||
                      (newOrder.productName ? { value: newOrder.productName, label: newOrder.productName } : null)
                    }
                    onChange={(option) => {
                      if (!option) {
                        onFieldChange('productName', '');
                        onFieldChange('unitPrice', '');
                      } else if (option.__isNew__) {
                        onFieldChange('productName', option.value);
                        onFieldChange('unitPrice', '');
                      } else {
                        handleProductSelect(option.value);
                      }
                    }}
                    isDisabled={isLoading}
                    isClearable
                    isSearchable
                    placeholder="Ürün seçin veya yazın..."
                    noOptionsMessage={() => "Ürün bulunamadı"}
                    formatCreateLabel={(inputValue) => `"${inputValue}" olarak ekle`}
                    styles={customSelectStyles}
                  />
                </div>

                <div className="form-group">
                  <label>Ürün Varyantı</label>
                  <input
                    type="text"
                    value={newOrder.productVariant || ''}
                    onChange={(e) => onFieldChange('productVariant', e.target.value)}
                    disabled={isLoading}
                    placeholder="Renk, desen vb. giriniz..."
                  />
                </div>

                <div className="form-group">
                  <label>Birim Tipi *</label>
                  <select
                    value={newOrder.aksesuarBirimTipi || 'adet'}
                    onChange={(e) => {
                      onFieldChange('aksesuarBirimTipi', e.target.value);
                      if (e.target.value === 'metre') {
                        onFieldChange('quantity', '1'); // Metre seçilince adet otomatik 1 olur
                        if (!newOrder.usedMeter) onFieldChange('usedMeter', '1');
                      } else {
                        onFieldChange('usedMeter', ''); // Adet seçilince metre sıfırlanır
                      }
                    }}
                    disabled={isLoading}
                    style={{ padding: '8px', border: '1px solid #cbd5e1', borderRadius: '4px', width: '100%' }}
                  >
                    <option value="adet">Adet</option>
                    <option value="metre">Metre</option>
                  </select>
                </div>

                {(!newOrder.aksesuarBirimTipi || newOrder.aksesuarBirimTipi === 'adet') ? (
                  <div className="form-group">
                    <label>Adet *</label>
                    <input
                      type="number"
                      step="0.01"
                      min="1"
                      value={newOrder.quantity}
                      onKeyDown={handleNumericKeyDown}
                      onChange={(e) => onFieldChange('quantity', e.target.value)}
                      required
                      disabled={isLoading}
                    />
                  </div>
                ) : (
                  <>
                    <div className="form-group">
                      <label>Metre *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={newOrder.usedMeter}
                        onKeyDown={handleNumericKeyDown}
                        onChange={(e) => onFieldChange('usedMeter', e.target.value)}
                        required
                        disabled={isLoading}
                      />
                    </div>
                    <div className="form-group">
                      <label>Adet *</label>
                      <input
                        type="number"
                        step="1"
                        min="1"
                        value={newOrder.quantity}
                        onKeyDown={handleNumericKeyDown}
                        onChange={(e) => onFieldChange('quantity', e.target.value)}
                        required
                        disabled={isLoading}
                      />
                    </div>
                  </>
                )}

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    Birim Fiyat (₺) *
                    <i
                      className={`fas ${showUnitCost ? 'fa-unlock' : 'fa-lock'}`}
                      style={{
                        fontSize: '12px',
                        cursor: 'pointer',
                        color: showUnitCost ? '#1a1a1a' : '#ccc',
                        padding: '4px'
                      }}
                      onClick={() => setShowUnitCost(!showUnitCost)}
                      title="Maliyet girişi için tıkla"
                    ></i>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={newOrder.unitPrice}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => {
                      onFieldChange('unitPrice', e.target.value);
                      if (e.target.value === '') {
                        onFieldChange('unitCost', '');
                      }
                    }}
                    required
                    disabled={isLoading}
                  />
                </div>

                {showUnitCost && (
                  <div className="form-group animate-fadeIn">
                    <label style={{ color: '#1a1a1a' }}>Birim Maliyet (₺)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newOrder.unitCost || ''}
                      onKeyDown={handleNumericKeyDown}
                      onChange={(e) => onFieldChange('unitCost', e.target.value)}
                      disabled={isLoading}
                      placeholder="Gizli maliyet"
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>İskonto (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={newOrder.discount}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('discount', e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                {newOrder.totalPrice && (
                  <div className="form-group">
                    <label>Toplam Tutar (₺)</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.totalPrice)}
                      readOnly
                      className="total-price"
                    />
                  </div>
                )}

                {newOrder.discountedPrice && parseFloat(newOrder.discount) > 0 && (
                  <div className="form-group">
                    <label>İndirimli Tutar (₺)</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.discountedPrice)}
                      readOnly
                      className="discounted-price"
                    />
                  </div>
                )}
              </>
            )}

            {/* Ray Sistemleri için özel alanlar */}
            {selectedProductType === 'raySistemi' && (
              <>
                <div className="form-group">
                  <label>Oda Adı *</label>
                  <input
                    type="text"
                    value={newOrder.roomName}
                    onChange={(e) => onFieldChange('roomName', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>Ürün Adı *</label>
                  <CreatableSelect
                    options={productOptions}
                    value={
                      productOptions.find(o => o.value === (productItems.find(p => p.name === newOrder.productName)?.id)) ||
                      (newOrder.productName ? { value: newOrder.productName, label: newOrder.productName } : null)
                    }
                    onChange={(option) => {
                      if (!option) {
                        onFieldChange('productName', '');
                        onFieldChange('unitPrice', '');
                      } else if (option.__isNew__) {
                        onFieldChange('productName', option.value);
                        onFieldChange('unitPrice', '');
                      } else {
                        handleProductSelect(option.value);
                      }
                    }}
                    isDisabled={isLoading}
                    isClearable
                    isSearchable
                    placeholder="Ürün seçin veya yazın..."
                    noOptionsMessage={() => "Ürün bulunamadı"}
                    formatCreateLabel={(inputValue) => `"${inputValue}" olarak ekle`}
                    styles={customSelectStyles}
                  />
                </div>

                <div className="form-group">
                  <label>Ürün Varyantı</label>
                  <input
                    type="text"
                    value={newOrder.productVariant || ''}
                    onChange={(e) => onFieldChange('productVariant', e.target.value)}
                    disabled={isLoading}
                    placeholder="Renk, desen vb. giriniz..."
                  />
                </div>

                <div className="form-group">
                  <label>En (cm) *</label>
                  <input
                    type="number"
                    step="1"
                    value={newOrder.width}
                    onChange={(e) => onFieldChange('width', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>Ölçü Tamamlama *</label>
                  <select
                    value={newOrder.olcuTamamlama || ''}
                    onChange={(e) => onFieldChange('olcuTamamlama', e.target.value)}
                    required
                    disabled={isLoading}
                  >
                    <option value="">Seçiniz</option>
                    <option value="25cm">25cm'e tamamla</option>
                    <option value="50cm">50cm'e tamamla</option>
                    <option value="urunBoyutu">Ürün boyutu</option>
                  </select>
                </div>

                {newOrder.usedMeter && (
                  <div className="form-group">
                    <label>Kullanılan Metre</label>
                    <input
                      type="text"
                      value={newOrder.usedMeter + ' m'}
                      readOnly
                      className="readonly-field"
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Birim Fiyat (₺) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={newOrder.unitPrice}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('unitPrice', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>Montaj (₺)<i className={`fas ${showMountingCost ? "fa-unlock" : "fa-lock"}`} style={{ fontSize: "12px", cursor: "pointer", color: showMountingCost ? "#1a1a1a" : "#ccc", padding: "4px" }} onClick={() => setShowMountingCost(!showMountingCost)} title="Maliyet girişi için tıkla"></i></label>
                  <input
                    type="number"
                    step="0.01"
                    value={newOrder.mounting || ''}
                    onChange={(e) => onFieldChange('mounting', e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                {showMountingCost && (
                  <div className="form-group animate-fadeIn">
                    <label style={{ color: "#1a1a1a" }}>Montaj Maliyeti (₺)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newOrder.mountingCost}
                      onKeyDown={handleNumericKeyDown}
                      onChange={(e) => onFieldChange("mountingCost", e.target.value)}
                      disabled={isLoading}
                      placeholder="Gizli maliyet"
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Adet *</label>
                  <input
                    type="number"
                    step="1"
                    min="1"
                    value={newOrder.quantity || ''}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('quantity', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>İskonto (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={newOrder.discount}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('discount', e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                {newOrder.totalPrice && (
                  <div className="form-group">
                    <label>Toplam Tutar (₺)</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.totalPrice)}
                      readOnly
                      className="total-price"
                    />
                  </div>
                )}

                {newOrder.discountedPrice && parseFloat(newOrder.discount) > 0 && (
                  <div className="form-group">
                    <label>İndirimli Tutar (₺)</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.discountedPrice)}
                      readOnly
                      className="discounted-price"
                    />
                  </div>
                )}
              </>
            )}

            {/* Rustik için özel alanlar */}
            {selectedProductType === 'rustik' && (
              <>
                <div className="form-group">
                  <label>Oda Adı *</label>
                  <input
                    type="text"
                    value={newOrder.roomName}
                    onChange={(e) => onFieldChange('roomName', e.target.value)}
                    required
                    disabled={isLoading}
                    placeholder="Örn: Salon"
                  />
                </div>

                <div className="form-group">
                  <label>Ürün Adı *</label>
                  <CreatableSelect
                    options={productOptions}
                    value={
                      productOptions.find(o => o.label === newOrder.productName) ||
                      (newOrder.productName ? { value: newOrder.productName, label: newOrder.productName } : null)
                    }
                    onChange={(option) => {
                      if (!option) {
                        onFieldChange('productName', '');
                        onFieldChange('unitPrice', '');
                      } else if (option.__isNew__) {
                        onFieldChange('productName', option.value);
                        onFieldChange('unitPrice', '');
                      } else {
                        handleProductSelect(option.value);
                      }
                    }}
                    isDisabled={isLoading}
                    isClearable
                    isSearchable
                    placeholder="Ürün seçin veya yazın..."
                    noOptionsMessage={() => "Ürün bulunamadı"}
                    formatCreateLabel={(inputValue) => `"${inputValue}" olarak ekle`}
                    styles={customSelectStyles}
                  />
                </div>
                <div className="form-group">
                  <label>Ürün Varyantı</label>
                  <input
                    type="text"
                    value={newOrder.productVariant || ''}
                    onChange={(e) => onFieldChange('productVariant', e.target.value)}
                    disabled={isLoading}
                    placeholder="Renk, desen vb. giriniz..."
                  />
                </div>

                <div className="form-group">
                  <label>En (cm) *</label>
                  <input
                    type="number"
                    value={newOrder.width}
                    onChange={(e) => onFieldChange('width', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>Kullanılan Cm</label>
                  <input
                    type="number"
                    value={newOrder.usedCm}
                    onChange={(e) => onFieldChange('usedCm', e.target.value)}
                    disabled={isLoading}
                    className="readonly-field"
                  />
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    Birim Fiyat (₺) *
                    <i
                      className={`fas ${showUnitCost ? 'fa-unlock' : 'fa-lock'}`}
                      style={{
                        fontSize: '12px',
                        cursor: 'pointer',
                        color: showUnitCost ? '#1a1a1a' : '#ccc',
                        padding: '4px'
                      }}
                      onClick={() => setShowUnitCost(!showUnitCost)}
                      title="Maliyet girişi için tıkla"
                    ></i>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={newOrder.unitPrice}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => {
                      onFieldChange('unitPrice', e.target.value);
                      if (e.target.value === '') {
                        onFieldChange('unitCost', '');
                      }
                    }}
                    required
                    disabled={isLoading}
                  />
                </div>

                {showUnitCost && (
                  <div className="form-group animate-fadeIn">
                    <label style={{ color: '#1a1a1a' }}>Birim Maliyet (₺)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newOrder.unitCost}
                      onKeyDown={handleNumericKeyDown}
                      onChange={(e) => onFieldChange('unitCost', e.target.value)}
                      disabled={isLoading}
                      placeholder="Gizli maliyet"
                    />
                  </div>
                )}

                {/* Ayak Ayarları */}
                <div className="form-group">
                  <label>Ayak Modeli *</label>
                  {adminSettings.rustikAyakModelList?.length > 0 ? (
                    <Select
                      options={adminSettings.rustikAyakModelList.map(m => ({ value: m, label: m }))}
                      value={newOrder.ayakModel ? { value: newOrder.ayakModel, label: newOrder.ayakModel } : null}
                      onChange={(option) => onFieldChange('ayakModel', option ? option.value : '')}
                      styles={customSelectStyles}
                      isClearable
                    />
                  ) : (
                    <input type="text" value={newOrder.ayakModel} onChange={(e) => onFieldChange('ayakModel', e.target.value)} required />
                  )}
                </div>

                <div className="form-group">
                  <label>Ayak Çapı *</label>
                  {adminSettings.rustikAyakCapList?.length > 0 ? (
                    <Select
                      options={adminSettings.rustikAyakCapList.map(m => ({ value: m, label: m }))}
                      value={newOrder.ayakCap ? { value: newOrder.ayakCap, label: newOrder.ayakCap } : null}
                      onChange={(option) => onFieldChange('ayakCap', option ? option.value : '')}
                      styles={customSelectStyles}
                      isClearable
                    />
                  ) : (
                    <input type="text" value={newOrder.ayakCap} onChange={(e) => onFieldChange('ayakCap', e.target.value)} required />
                  )}
                </div>

                <div className="form-group">
                  <label>Ayak Uzunluğu *</label>
                  {adminSettings.rustikAyakUzunlukList?.length > 0 ? (
                    <Select
                      options={adminSettings.rustikAyakUzunlukList.map(m => ({ value: m, label: m }))}
                      value={newOrder.ayakUzunluk ? { value: newOrder.ayakUzunluk, label: newOrder.ayakUzunluk } : null}
                      onChange={(option) => onFieldChange('ayakUzunluk', option ? option.value : '')}
                      styles={customSelectStyles}
                      isClearable
                    />
                  ) : (
                    <input type="text" value={newOrder.ayakUzunluk} onChange={(e) => onFieldChange('ayakUzunluk', e.target.value)} required />
                  )}
                </div>

                <div className="form-group">
                  <label>Renk Kodu *</label>
                  {adminSettings.rustikAyakRenkKoduList?.length > 0 ? (
                    <Select
                      options={adminSettings.rustikAyakRenkKoduList.map(m => ({ value: m, label: m }))}
                      value={newOrder.ayakRenkKodu ? { value: newOrder.ayakRenkKodu, label: newOrder.ayakRenkKodu } : null}
                      onChange={(option) => onFieldChange('ayakRenkKodu', option ? option.value : '')}
                      styles={customSelectStyles}
                      isClearable
                    />
                  ) : (
                    <input type="text" value={newOrder.ayakRenkKodu} onChange={(e) => onFieldChange('ayakRenkKodu', e.target.value)} required />
                  )}
                </div>

                <div className="form-group">
                  <label>Ayak Sayısı *</label>
                  <Select
                    options={[1, 2, 3, 4, 5].map(v => ({ value: v, label: v }))}
                    value={newOrder.ayakSayisi ? { value: newOrder.ayakSayisi, label: newOrder.ayakSayisi } : null}
                    onChange={(option) => onFieldChange('ayakSayisi', option ? option.value : '')}
                    styles={customSelectStyles}
                  />
                </div>

                <div className="form-group">
                  <label>Ek Ayak Sayısı</label>
                  <Select
                    options={[1, 2, 3, 4, 5].map(v => ({ value: v, label: v }))}
                    value={newOrder.ekAyakSayisi ? { value: newOrder.ekAyakSayisi, label: newOrder.ekAyakSayisi } : null}
                    onChange={(option) => onFieldChange('ekAyakSayisi', option ? option.value : '')}
                    styles={customSelectStyles}
                    isClearable
                  />
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    Ayak Ücreti (₺)
                    <i
                      className={`fas ${showAyakCost ? 'fa-unlock' : 'fa-lock'}`}
                      style={{
                        fontSize: '12px',
                        cursor: 'pointer',
                        color: showAyakCost ? '#1a1a1a' : '#ccc',
                        padding: '4px'
                      }}
                      onClick={() => setShowAyakCost(!showAyakCost)}
                      title="Maliyet girişi için tıkla"
                    ></i>
                  </label>
                  <input type="number" step="0.01" min="0" onKeyDown={handleNumericKeyDown} value={newOrder.ayakUcreti} onChange={(e) => onFieldChange('ayakUcreti', e.target.value)} />
                </div>

                {showAyakCost && (
                  <div className="form-group animate-fadeIn">
                    <label style={{ color: '#1a1a1a' }}>Ayak Maliyeti (₺)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newOrder.ayakUcretiCost || ''}
                      onKeyDown={handleNumericKeyDown}
                      onChange={(e) => onFieldChange('ayakUcretiCost', e.target.value)}
                      disabled={isLoading}
                      placeholder="Gizli maliyet"
                    />
                  </div>
                )}

                <div className="form-group">
                  <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>Montaj (₺)<i className={`fas ${showMountingCost ? "fa-unlock" : "fa-lock"}`} style={{ fontSize: "12px", cursor: "pointer", color: showMountingCost ? "#1a1a1a" : "#ccc", padding: "4px" }} onClick={() => setShowMountingCost(!showMountingCost)} title="Maliyet girişi için tıkla"></i></label>
                  <input type="number" step="0.01" value={newOrder.mounting} onChange={(e) => onFieldChange('mounting', e.target.value)} />
                </div>

                {showMountingCost && (
                  <div className="form-group animate-fadeIn">
                    <label style={{ color: '#1a1a1a' }}>Montaj Maliyeti (₺)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newOrder.mountingCost}
                      onKeyDown={handleNumericKeyDown}
                      onChange={(e) => onFieldChange('mountingCost', e.target.value)}
                      disabled={isLoading}
                      placeholder="Gizli maliyet"
                    />
                  </div>
                )}

                {/* Ray Seçenekleri ve Aksesuarlar */}
                <div style={{ gridColumn: '1 / -1', padding: '15px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', marginTop: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h4 style={{ margin: 0, fontSize: '15px', color: '#1a1a1a' }}>
                      <i className="fas fa-road" style={{ marginRight: '8px' }}></i>
                      Ray Seçenekleri
                    </h4>
                    <button
                      type="button"
                      onClick={() => {
                        const newList = [...(newOrder.rustikRaySecenekleri || []), { name: '', price: '0', quantity: newOrder.width || '1' }];
                        onFieldChange('rustikRaySecenekleri', newList);
                      }}
                      style={{ padding: '6px 12px', backgroundColor: '#0f172a', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}
                    >
                      <i className="fas fa-plus" style={{ marginRight: '5px' }}></i>
                      Seçenek Ekle
                    </button>
                  </div>

                  {Array.isArray(newOrder.rustikRaySecenekleri) && newOrder.rustikRaySecenekleri.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
                      {newOrder.rustikRaySecenekleri.map((ray, idx) => (
                        <div key={idx} className="rustik-item-row">
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: '11px' }}>Seçenek Adı</label>
                            <input
                              type="text"
                              value={ray.name}
                              onChange={(e) => {
                                const newList = [...newOrder.rustikRaySecenekleri];
                                newList[idx].name = e.target.value;
                                onFieldChange('rustikRaySecenekleri', newList);
                              }}
                              placeholder="Fırtına Ray vb."
                              style={{ padding: '6px' }}
                            />
                          </div>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              Fiyat (₺)
                              <i
                                className={`fas ${showRustikRayCost ? 'fa-unlock' : 'fa-lock'}`}
                                style={{ fontSize: '10px', cursor: 'pointer', color: showRustikRayCost ? '#1a1a1a' : '#ccc' }}
                                onClick={() => setShowRustikRayCost(!showRustikRayCost)}
                                title="Maliyet girişi için tıkla"
                              ></i>
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={ray.price}
                              onKeyDown={handleNumericKeyDown}
                              onChange={(e) => {
                                const newList = [...newOrder.rustikRaySecenekleri];
                                newList[idx].price = e.target.value;
                                newList[idx].cost = newList[idx].cost || '0';
                                const p = parseFloat(e.target.value) || 0;
                                const q = parseFloat(newList[idx].quantity) || 0;
                                const c = parseFloat(newList[idx].cost) || 0;
                                newList[idx].total = (p * q).toFixed(2);
                                newList[idx].costTotal = (c * q).toFixed(2);
                                onFieldChange('rustikRaySecenekleri', newList);
                              }}
                              style={{ padding: '6px' }}
                            />
                            {showRustikRayCost && (
                              <div style={{ marginTop: '5px' }}>
                                <label style={{ fontSize: '10px', color: '#1a1a1a', display: 'block' }}>Gizli Maliyet (₺)</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={ray.cost || '0'}
                                  onKeyDown={handleNumericKeyDown}
                                  onChange={(e) => {
                                    const newList = [...newOrder.rustikRaySecenekleri];
                                    newList[idx].cost = e.target.value;
                                    const p = parseFloat(newList[idx].price) || 0;
                                    const q = parseFloat(newList[idx].quantity) || 0;
                                    const c = parseFloat(e.target.value) || 0;
                                    newList[idx].total = (p * q).toFixed(2);
                                    newList[idx].costTotal = (c * q).toFixed(2);
                                    onFieldChange('rustikRaySecenekleri', newList);
                                  }}
                                  style={{ padding: '4px', fontSize: '11px', border: '1px solid #1a1a1a' }}
                                />
                              </div>
                            )}
                          </div>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: '11px' }}>En (cm)</label>
                            <input
                              type="number"
                              step="1"
                              min="1"
                              onKeyDown={handleNumericKeyDown}
                              value={ray.quantity || ''}
                              onChange={(e) => {
                                const newList = [...newOrder.rustikRaySecenekleri];
                                newList[idx].quantity = e.target.value;
                                const p = parseFloat(newList[idx].price) || 0;
                                const q = parseFloat(e.target.value) || 0;
                                const c = parseFloat(newList[idx].cost || 0) || 0;
                                newList[idx].total = (p * q).toFixed(2);
                                newList[idx].costTotal = (c * q).toFixed(2);
                                onFieldChange('rustikRaySecenekleri', newList);
                              }}
                              style={{ padding: '6px' }}
                            />
                          </div>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: '11px' }}>Tutar (₺)</label>
                            <input
                              type="text"
                              value={formatPrice(parseFloat(ray.price) * (parseFloat(newOrder.usedCm) / 100) || 0)}
                              readOnly
                              style={{ padding: '6px', backgroundColor: '#f1f5f9' }}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const newList = newOrder.rustikRaySecenekleri.filter((_, i) => i !== idx);
                              onFieldChange('rustikRaySecenekleri', newList);
                            }}
                            style={{ padding: '8px', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ fontSize: '13px', color: '#64748b', textAlign: 'center', margin: '10px 0 20px 0' }}>Henüz ray seçeneği eklenmemiş.</p>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', borderTop: '1px solid #e2e8f0', paddingTop: '15px' }}>
                    <h4 style={{ margin: 0, fontSize: '15px', color: '#1a1a1a' }}>
                      <i className="fas fa-tags" style={{ marginRight: '8px' }}></i>
                      Aksesuarlar
                    </h4>
                    <button
                      type="button"
                      onClick={() => {
                        const newList = [...(newOrder.rustikAksesuarlar || []), { name: '', price: '0', cost: '0', quantity: '1', total: '0', costTotal: '0' }];
                        onFieldChange('rustikAksesuarlar', newList);
                      }}
                      style={{ padding: '6px 12px', backgroundColor: '#0f172a', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}
                    >
                      <i className="fas fa-plus" style={{ marginRight: '5px' }}></i>
                      Aksesuar Ekle
                    </button>
                  </div>

                  {Array.isArray(newOrder.rustikAksesuarlar) && newOrder.rustikAksesuarlar.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {newOrder.rustikAksesuarlar.map((acc, idx) => (
                        <div key={idx} className="rustik-item-row">
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: '11px' }}>Aksesuar Adı</label>
                            <input
                              type="text"
                              value={acc.name}
                              onChange={(e) => {
                                const newList = [...newOrder.rustikAksesuarlar];
                                newList[idx].name = e.target.value;
                                onFieldChange('rustikAksesuarlar', newList);
                              }}
                              placeholder="Fisto, Boncuk vb."
                              style={{ padding: '6px' }}
                            />
                          </div>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              Fiyat (₺)
                              <i
                                className={`fas ${showRustikAksesuarCost ? 'fa-unlock' : 'fa-lock'}`}
                                style={{ fontSize: '10px', cursor: 'pointer', color: showRustikAksesuarCost ? '#1a1a1a' : '#ccc' }}
                                onClick={() => setShowRustikAksesuarCost(!showRustikAksesuarCost)}
                                title="Maliyet girişi için tıkla"
                              ></i>
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={acc.price}
                              onKeyDown={handleNumericKeyDown}
                              onChange={(e) => {
                                const newList = [...newOrder.rustikAksesuarlar];
                                newList[idx].price = e.target.value;
                                newList[idx].cost = newList[idx].cost || '0';
                                const p = parseFloat(e.target.value) || 0;
                                const q = parseFloat(newList[idx].quantity) || 0;
                                const c = parseFloat(newList[idx].cost) || 0;
                                newList[idx].total = (p * q).toFixed(2);
                                newList[idx].costTotal = (c * q).toFixed(2);
                                onFieldChange('rustikAksesuarlar', newList);
                              }}
                              style={{ padding: '6px' }}
                            />
                            {showRustikAksesuarCost && (
                              <div style={{ marginTop: '5px' }}>
                                <label style={{ fontSize: '10px', color: '#1a1a1a', display: 'block' }}>Gizli Maliyet (₺)</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={acc.cost || '0'}
                                  onKeyDown={handleNumericKeyDown}
                                  onChange={(e) => {
                                    const newList = [...newOrder.rustikAksesuarlar];
                                    newList[idx].cost = e.target.value;
                                    const p = parseFloat(newList[idx].price) || 0;
                                    const q = parseFloat(newList[idx].quantity) || 0;
                                    const c = parseFloat(e.target.value) || 0;
                                    newList[idx].total = (p * q).toFixed(2);
                                    newList[idx].costTotal = (c * q).toFixed(2);
                                    onFieldChange('rustikAksesuarlar', newList);
                                  }}
                                  style={{ padding: '4px', fontSize: '11px', border: '1px solid #1a1a1a' }}
                                />
                              </div>
                            )}
                          </div>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: '11px' }}>Adet</label>
                            <input
                              type="number"
                              step="1"
                              min="1"
                              onKeyDown={handleNumericKeyDown}
                              value={acc.quantity}
                              onChange={(e) => {
                                const newList = [...newOrder.rustikAksesuarlar];
                                newList[idx].quantity = e.target.value;
                                const p = parseFloat(newList[idx].price) || 0;
                                const q = parseFloat(e.target.value) || 0;
                                const c = parseFloat(newList[idx].cost) || 0;
                                newList[idx].total = (p * q).toFixed(2);
                                newList[idx].costTotal = (c * q).toFixed(2);
                                onFieldChange('rustikAksesuarlar', newList);
                              }}
                              style={{ padding: '6px' }}
                            />
                          </div>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: '11px' }}>Tutar (₺)</label>
                            <input
                              type="text"
                              value={formatPrice(parseFloat(acc.price) * parseInt(acc.quantity) || 0)}
                              readOnly
                              style={{ padding: '6px', backgroundColor: '#f1f5f9' }}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const newList = newOrder.rustikAksesuarlar.filter((_, i) => i !== idx);
                              onFieldChange('rustikAksesuarlar', newList);
                            }}
                            style={{ padding: '8px', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ fontSize: '13px', color: '#64748b', textAlign: 'center', margin: '10px 0' }}>Henüz aksesuar eklenmemiş.</p>
                  )}
                </div>

                <div className="form-group">
                  <label>Adet *</label>
                  <input
                    type="number"
                    value={newOrder.quantity}
                    onChange={(e) => onFieldChange('quantity', e.target.value)}
                    min="1"
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>İskonto (%)</label>
                  <input
                    type="number"
                    value={newOrder.discount}
                    onChange={(e) => onFieldChange('discount', e.target.value)}
                    min="0"
                    max="100"
                    disabled={isLoading}
                  />
                </div>

                {newOrder.totalPrice && (
                  <div className="form-group">
                    <label>Toplam Tutar (₺)</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.totalPrice)}
                      readOnly
                      className="total-price"
                    />
                  </div>
                )}

                {newOrder.discountedPrice && parseFloat(newOrder.discount) > 0 && (
                  <div className="form-group">
                    <label>İndirimli Tutar (₺)</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.discountedPrice)}
                      readOnly
                      className="discounted-price"
                    />
                  </div>
                )}
              </>
            )}


            <div className="form-group">
              <label>Not</label>
              <textarea
                value={newOrder.notes}
                onChange={(e) => onFieldChange('notes', e.target.value)}
                rows="2"
                disabled={isLoading}
              />
            </div>
          </div>
        )
        }

        {/* Firma WhatsApp Bildirim Bölümü (Tam Genişlikte Yan Yana Modern Kart Izgarası) */}
        {selectedProductType && (newOrder.productName || (selectedProductType === 'plise' && newOrder.pliseProduct1Name)) && newOrder.roomName && (
          <div className="order-firma-dispatch-section">
            <div className="firma-dispatch-header">
              <div className="firma-dispatch-title">
                <div className="firma-dispatch-icon-badge">
                  <i className="fab fa-whatsapp"></i>
                </div>
                <div>
                  <span className="firma-dispatch-heading">Firma Sipariş Bildirimi (WhatsApp)</span>
                  <span className="firma-dispatch-subheading">
                    {currentFirmaCards.length > 1
                      ? 'Sipariş detaylarını ve ölçüleri ilgili tedarikçi / atölyelere doğrudan WhatsApp üzerinden iletin'
                      : 'Sipariş detaylarını tedarikçi firmaya doğrudan WhatsApp üzerinden iletin'}
                  </span>
                </div>
              </div>
            </div>

            <div className={`multi-firma-grid items-${currentFirmaCards.length}`}>
              {currentFirmaCards.map((item) => (
                <div key={item.id} className="firma-card" style={{ borderTop: `3px solid ${item.color}` }}>
                  <div className="firma-card-header">
                    <div className="firma-card-label">
                      <span className="firma-card-icon-wrap" style={{ color: item.color, backgroundColor: `${item.color}15` }}>
                        <i className={item.icon}></i>
                      </span>
                      <span>{item.label}</span>
                    </div>
                    {item.lastSent && (
                      <span className="firma-card-sent" title={`${item.lastSent.name} firmasına iletildi`}>
                        <i className="fas fa-check-circle"></i> {item.lastSent.name}
                      </span>
                    )}
                  </div>
                  <div className="firma-card-controls">
                    <div className="firma-select-wrapper">
                      <Select
                        options={companies.map(firma => ({ value: firma.id, label: firma.name }))}
                        value={item.value ? { value: item.value, label: companies.find(f => f.id === item.value)?.name } : null}
                        onChange={(option) => item.setter(option ? option.value : '')}
                        className="firma-select"
                        isDisabled={isLoading}
                        isClearable
                        isSearchable
                        placeholder="Firma Seçin"
                        noOptionsMessage={() => "Firma bulunamadı"}
                        filterOption={(option, inputValue) => option.label.toLowerCase().includes(inputValue.toLowerCase())}
                        styles={customSelectStyles}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={item.onSend}
                      disabled={!item.value || isLoading}
                      className="whatsapp-btn whatsapp-btn-compact"
                      title={item.value ? `${item.label} bilgilerini WhatsApp ile gönder` : 'Önce firma seçin'}
                    >
                      <i className="fab fa-whatsapp"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="form-actions">
          <div className="action-buttons">
            <button
              type="submit"
              className="save-btn"
              disabled={isLoading || !selectedProductType}
            >
              {isLoading ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="cancel-btn"
              disabled={isLoading}
            >
              İptal
            </button>
          </div>
        </div>
      </form >
    </div >
  );
};

export default OrderForm; 